// Cuisine-aware food alternatives system

import { AlternativeRecipe, getRecipeForAlternative, createFallbackRecipe } from '../data/recipes';

/**
 * Generate a unique stable ID for an alternative based on its name
 * This ensures 1:1 mapping between alternative card and recipe
 */
/** Get recipe from catalog or create fallback so every alternative has a View Recipe button */
function getOrCreateRecipe(alt: FoodAlternative, cuisine?: string | null): AlternativeRecipe {
  return getRecipeForAlternative(alt.name, cuisine || undefined) || createFallbackRecipe(alt.name, alt.description, cuisine || undefined);
}

function generateAlternativeId(alternativeName: string, index?: number): string {
  // Create a stable ID from the alternative name
  const normalizedName = alternativeName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  // Add index if provided to ensure uniqueness even with duplicate names
  return index !== undefined ? `alt_${normalizedName}_${index}` : `alt_${normalizedName}`;
}

export interface HealthyBrand {
  name: string;
  description: string;
  acneImpactScore: number;
  whyHealthy: string; // Why this brand is better for skin/acne
  tags: string[];
}

export interface FoodAlternative {
  id?: string; // CRITICAL: Unique stable ID for 1:1 mapping with recipe (generated if missing)
  name: string;
  description: string;
  acneImpactScore: number;
  tags: string[];
  recipe?: AlternativeRecipe; // CRITICAL: Each alternative has its own recipe object
  brands?: HealthyBrand[]; // Healthy brand recommendations for this alternative
}

type DishType = 'main' | 'side' | 'dessert' | 'drink' | 'snack' | 'breakfast';

// Food category for more precise matching
type FoodCategory = 
  | 'cereal' 
  | 'cake' 
  | 'cookie' 
  | 'brownies'
  | 'ice_cream' 
  | 'pizza' 
  | 'burger' 
  | 'pasta' 
  | 'fries' 
  | 'chips' 
  | 'soda' 
  | 'pancake' 
  | 'waffle'
  | 'donut'
  | 'pie'
  | 'chocolate'
  | 'candy'
  | 'bread'
  | 'muffin'
  | 'croissant'
  | 'other';

// Cuisine-specific alternatives organized by dish type
export const CUISINE_ALTERNATIVES: Record<string, Record<DishType, FoodAlternative[]>> = {
  indian: {
    main: [
      {
        name: 'Lighter sambar with brown rice',
        description: 'Sambar with less oil, served with brown rice instead of white. More fiber, lower glycemic impact, better for skin.',
        acneImpactScore: 35,
        tags: ['indian', 'sambar', 'brown rice', 'lower oil', 'whole grain']
      },
      {
        name: 'Rasam with quinoa or millet',
        description: 'Traditional rasam with quinoa or millet instead of white rice. Higher protein, lower glycemic, more nutrients.',
        acneImpactScore: 32,
        tags: ['indian', 'rasam', 'quinoa', 'millet', 'whole grain']
      },
      {
        name: 'Vegetable-loaded sambar/rasam',
        description: 'Extra vegetables added to sambar/rasam, less oil, served with whole grain. More nutrients, less refined carbs.',
        acneImpactScore: 38,
        tags: ['indian', 'sambar', 'rasam', 'vegetables', 'whole grain', 'lower oil']
      },
      {
        name: 'Sambar/rasam with steamed vegetables',
        description: 'Traditional soup with steamed vegetables on the side instead of fried. Less oil, more nutrients preserved.',
        acneImpactScore: 36,
        tags: ['indian', 'sambar', 'rasam', 'steamed vegetables', 'lower oil']
      },
      {
        name: 'Lighter paneer tikka with whole-wheat roti',
        description: 'Grilled or baked paneer with spices, minimal oil, and a yogurt or tomato base instead of heavy cream. Paired with fiber-rich whole-wheat roti. Lower in saturated fat and glycemic load.',
        acneImpactScore: 45,
        tags: ['indian', 'main', 'paneer', 'lower dairy', 'whole grain', 'grilled']
      },
      {
        name: 'Chana masala with whole-wheat roti',
        description: 'Chickpea curry high in fiber and plant protein, with less dairy and added fat. Tomatoes and spices provide flavor without heavy cream.',
        acneImpactScore: 40,
        tags: ['indian', 'main', 'chickpeas', 'fiberRich', 'lower dairy', 'whole grain']
      },
      {
        name: 'Dal tadka with brown rice or millet',
        description: 'Lentil-based protein, lighter on dairy and more fiber. Tempered with minimal oil and served with whole grains instead of white rice.',
        acneImpactScore: 35,
        tags: ['indian', 'main', 'lentils', 'fiberRich', 'whole grain', 'lower dairy']
      },
      {
        name: 'Mixed vegetable curry with less oil + whole-wheat roti',
        description: 'More vegetables, less cream and butter; supports more stable blood sugar. Uses whole-wheat roti for better glycemic control.',
        acneImpactScore: 38,
        tags: ['indian', 'main', 'vegetables', 'lower oil', 'whole grain']
      },
      {
        name: 'Tandoori chicken with vegetables and brown rice',
        description: 'Grilled chicken marinated in yogurt and spices, served with roasted vegetables and whole grain rice. Less oil than butter-based curries.',
        acneImpactScore: 42,
        tags: ['indian', 'main', 'grilled', 'protein', 'vegetables', 'whole grain']
      },
      {
        name: 'Rajma (kidney bean curry) with brown rice',
        description: 'High-fiber kidney bean curry with tomatoes and spices, minimal dairy. Excellent plant protein source.',
        acneImpactScore: 38,
        tags: ['indian', 'main', 'beans', 'fiberRich', 'whole grain', 'lower dairy']
      }
    ],
    side: [
      {
        name: 'Raita with cucumber and mint',
        description: 'Yogurt-based side with vegetables and herbs. Lower in fat than heavy cream-based sides.',
        acneImpactScore: 30,
        tags: ['indian', 'side', 'yogurt', 'vegetables', 'probiotics']
      },
      {
        name: 'Steamed vegetables with spices',
        description: 'Steamed instead of fried, with Indian spices for flavor. Lower oil content.',
        acneImpactScore: 25,
        tags: ['indian', 'side', 'vegetables', 'steamed', 'lower oil']
      }
    ],
    snack: [
      {
        name: 'Baked samosas (instead of fried)',
        description: 'Same flavors, less oil - baked version of the classic snack. Filling can include vegetables and spices.',
        acneImpactScore: 40,
        tags: ['indian', 'snack', 'samosa', 'baked', 'lower oil']
      },
      {
        name: 'Roasted chana (chickpeas)',
        description: 'Traditional Indian snack - high fiber, protein, and healthy spices. No deep-frying required.',
        acneImpactScore: 25,
        tags: ['indian', 'snack', 'chickpeas', 'fiberRich', 'protein', 'roasted']
      },
      {
        name: 'Baked pakora',
        description: 'Vegetable fritters baked instead of deep-fried, same spices and flavors with less oil.',
        acneImpactScore: 38,
        tags: ['indian', 'snack', 'pakora', 'baked', 'vegetables', 'lower oil']
      },
      {
        name: 'Sprouted moong chaat',
        description: 'Sprouted mung beans with vegetables, spices, and lemon. High in fiber and protein, no frying.',
        acneImpactScore: 20,
        tags: ['indian', 'snack', 'sprouts', 'fiberRich', 'protein', 'raw']
      }
    ],
    dessert: [],
    drink: [],
    breakfast: []
  },
  
  italian: {
    main: [
      {
        name: 'Tomato-based pasta with veggies & light cheese',
        description: 'Marinara or pomodoro sauce instead of creamy alfredo. Add vegetables and use minimal cheese for flavor. Lower in dairy and saturated fat.',
        acneImpactScore: 45,
        tags: ['italian', 'main', 'pasta', 'tomato', 'lower dairy', 'vegetables']
      },
      {
        name: 'Whole-wheat spaghetti with olive oil, garlic, and vegetables',
        description: 'Complex carbs from whole grains, healthy fats from olive oil, and fiber from vegetables. No heavy cream or excessive cheese.',
        acneImpactScore: 35,
        tags: ['italian', 'main', 'pasta', 'whole grain', 'healthyFats', 'vegetables']
      },
      {
        name: 'Thin-crust veggie pizza with light cheese',
        description: 'Thinner whole wheat crust, light cheese, and extra vegetables instead of processed meats. Less refined carbs and dairy.',
        acneImpactScore: 45,
        tags: ['italian', 'main', 'pizza', 'lighter cheese', 'whole grain', 'vegetables']
      },
      {
        name: 'Cauliflower crust pizza',
        description: 'Lower-carb, higher-fiber crust alternative with vegetable base. Top with light cheese and vegetables.',
        acneImpactScore: 40,
        tags: ['italian', 'main', 'pizza', 'vegetables', 'lower carb', 'lower dairy']
      },
      {
        name: 'Margherita pizza with fresh basil',
        description: 'Simple tomato sauce, light mozzarella, and fresh herbs - less processed than typical pizza with multiple cheeses.',
        acneImpactScore: 48,
        tags: ['italian', 'main', 'pizza', 'lighter cheese', 'tomato', 'herbs']
      },
      {
        name: 'Zucchini noodles (zoodles) with marinara',
        description: 'Spiralized zucchini instead of pasta, topped with tomato sauce and light cheese. Lower in refined carbs.',
        acneImpactScore: 30,
        tags: ['italian', 'main', 'vegetables', 'lower carb', 'tomato']
      }
    ],
    side: [
      {
        name: 'Roasted vegetables with herbs',
        description: 'Roasted instead of fried, with Italian herbs and olive oil. Lower oil than fried sides.',
        acneImpactScore: 25,
        tags: ['italian', 'side', 'vegetables', 'roasted', 'herbs']
      },
      {
        name: 'Mixed green salad with olive oil dressing',
        description: 'Fresh greens with a light olive oil and lemon dressing instead of creamy dressings.',
        acneImpactScore: 20,
        tags: ['italian', 'side', 'salad', 'vegetables', 'healthyFats']
      }
    ],
    snack: [],
    dessert: [
      {
        name: 'Fresh fruit with a drizzle of honey',
        description: 'Naturally sweet fruit instead of heavy desserts. Minimal added sugar.',
        acneImpactScore: 25,
        tags: ['italian', 'dessert', 'fruit', 'lower sugar']
      }
    ],
    drink: [],
    breakfast: []
  },
  
  mexican: {
    main: [
      {
        name: 'Grilled chicken or bean tacos on corn tortillas with extra veggies',
        description: 'Grilled instead of fried, corn tortillas (whole grain), and plenty of vegetables. Less cheese and sour cream.',
        acneImpactScore: 42,
        tags: ['mexican', 'main', 'tacos', 'grilled', 'whole grain', 'vegetables', 'lower dairy']
      },
      {
        name: 'Burrito bowl with rice/beans, salsa, and veggies',
        description: 'All the flavors without the fried shell. Brown rice, beans, vegetables, and salsa. Skip the heavy cheese and sour cream.',
        acneImpactScore: 38,
        tags: ['mexican', 'main', 'burrito', 'whole grain', 'beans', 'fiberRich', 'vegetables']
      },
      {
        name: 'Quesadilla with less cheese and more veggies on whole-wheat or corn tortilla',
        description: 'Whole grain tortilla, light cheese, and extra vegetables. Grilled instead of fried in lots of oil.',
        acneImpactScore: 45,
        tags: ['mexican', 'main', 'quesadilla', 'whole grain', 'lighter cheese', 'vegetables', 'grilled']
      },
      {
        name: 'Grilled fish tacos with cabbage slaw',
        description: 'Grilled fish instead of fried, with fresh cabbage slaw and light dressing. Corn tortillas and minimal cheese.',
        acneImpactScore: 40,
        tags: ['mexican', 'main', 'tacos', 'grilled', 'fish', 'vegetables', 'lower dairy']
      },
      {
        name: 'Black bean and vegetable enchiladas with light cheese',
        description: 'Plant-based filling with vegetables, topped with light cheese and tomato sauce instead of heavy cream sauce.',
        acneImpactScore: 42,
        tags: ['mexican', 'main', 'beans', 'vegetables', 'lighter cheese', 'lower dairy']
      }
    ],
    side: [
      {
        name: 'Black beans with cilantro',
        description: 'High-fiber beans with fresh herbs. No added fat or dairy.',
        acneImpactScore: 25,
        tags: ['mexican', 'side', 'beans', 'fiberRich', 'herbs']
      },
      {
        name: 'Grilled vegetables with lime',
        description: 'Grilled vegetables with fresh lime juice instead of fried sides.',
        acneImpactScore: 20,
        tags: ['mexican', 'side', 'vegetables', 'grilled']
      }
    ],
    snack: [
      {
        name: 'Baked tortilla chips with salsa',
        description: 'Baked instead of fried chips, served with fresh salsa. Lower in oil and processed fats.',
        acneImpactScore: 35,
        tags: ['mexican', 'snack', 'chips', 'baked', 'lower oil']
      }
    ],
    dessert: [],
    drink: [],
    breakfast: []
  },
  
  american_fast_food: {
    main: [
      {
        name: 'Grilled chicken or turkey burger on whole-grain bun with salad',
        description: 'Leaner protein, whole grain bun, and side salad instead of fries. Less processed meat and refined carbs.',
        acneImpactScore: 45,
        tags: ['american', 'main', 'burger', 'whole grain', 'lean protein', 'vegetables']
      },
      {
        name: 'Veggie burger with avocado',
        description: 'Plant-based patty with healthy fats from avocado, whole grain bun. Less processed than beef burgers.',
        acneImpactScore: 40,
        tags: ['american', 'main', 'burger', 'vegetables', 'healthyFats', 'whole grain']
      },
      {
        name: 'Grilled chicken sandwich',
        description: 'Grilled instead of fried, whole grain bread, light on sauces. Side salad instead of fries.',
        acneImpactScore: 42,
        tags: ['american', 'main', 'grilled', 'whole grain', 'lean protein']
      },
      {
        name: 'Baked chicken tenders with vegetables',
        description: 'Same crispy texture with breadcrumbs, baked instead of deep-fried. Served with vegetables instead of fries.',
        acneImpactScore: 45,
        tags: ['american', 'main', 'chicken', 'baked', 'protein', 'vegetables']
      },
      {
        name: 'Air-fried chicken',
        description: 'Crispy chicken using air fryer with 70-80% less oil than deep-fried. Served with vegetables.',
        acneImpactScore: 42,
        tags: ['american', 'main', 'chicken', 'air-fried', 'lower oil']
      }
    ],
    side: [
      {
        name: 'Oven-baked potato wedges',
        description: 'Baked with a little olive oil instead of deep frying to reduce oil load. Same satisfying texture.',
        acneImpactScore: 45,
        tags: ['american', 'side', 'fries', 'baked', 'potato', 'lower oil']
      },
      {
        name: 'Baked sweet potato fries',
        description: 'Lower glycemic index than white potatoes, more nutrients, baked instead of fried.',
        acneImpactScore: 35,
        tags: ['american', 'side', 'fries', 'baked', 'sweet potato']
      },
      {
        name: 'Side salad with light dressing',
        description: 'Fresh vegetables with olive oil-based dressing instead of creamy dressings.',
        acneImpactScore: 20,
        tags: ['american', 'side', 'salad', 'vegetables', 'healthyFats']
      }
    ],
    snack: [],
    dessert: [],
    drink: [
      {
        name: 'Sparkling water with lemon or berries',
        description: 'Zero-sugar alternative that still feels like a treat and is more skin-friendly than soda.',
        acneImpactScore: 10,
        tags: ['american', 'drink', 'soda alternative', 'zero sugar']
      },
      {
        name: 'Unsweetened iced tea',
        description: 'Refreshing alternative to sugary sodas and milkshakes. Can add lemon for flavor.',
        acneImpactScore: 15,
        tags: ['american', 'drink', 'tea', 'zero sugar']
      }
    ],
    breakfast: []
  },
  
  jamaican: {
    main: [
      {
        name: 'Grilled jerk chicken with less sugary glaze and extra veggies',
        description: 'Grilled instead of fried, reduced sugar in the marinade, served with roasted vegetables. Lower in added sugar and oil.',
        acneImpactScore: 42,
        tags: ['jamaican', 'main', 'chicken', 'grilled', 'lower sugar', 'vegetables']
      },
      {
        name: 'Baked jerk fish with vegetables',
        description: 'Jerk-spiced fish baked instead of fried, with vegetables and brown rice. Less oil and sugar.',
        acneImpactScore: 38,
        tags: ['jamaican', 'main', 'fish', 'baked', 'lower sugar', 'vegetables', 'whole grain']
      },
      {
        name: 'Rice and peas with more beans and less added fat',
        description: 'Traditional rice and beans with more beans for fiber, less coconut milk and oil. Served with vegetables.',
        acneImpactScore: 40,
        tags: ['jamaican', 'main', 'rice', 'beans', 'fiberRich', 'whole grain', 'lower fat']
      },
      {
        name: 'Stewed chicken with vegetables',
        description: 'Chicken stewed with vegetables and spices, less oil than fried versions. Served with brown rice.',
        acneImpactScore: 42,
        tags: ['jamaican', 'main', 'chicken', 'stewed', 'vegetables', 'whole grain']
      }
    ],
    side: [
      {
        name: 'Baked plantains instead of deep-fried',
        description: 'Ripe plantains baked with minimal oil instead of deep-fried. Naturally sweet, less processed fat.',
        acneImpactScore: 40,
        tags: ['jamaican', 'side', 'plantains', 'baked', 'lower oil']
      },
      {
        name: 'Steamed callaloo',
        description: 'Steamed leafy greens with spices. High in nutrients, minimal oil.',
        acneImpactScore: 20,
        tags: ['jamaican', 'side', 'vegetables', 'steamed', 'greens']
      }
    ],
    snack: [],
    dessert: [],
    drink: [],
    breakfast: []
  },
  
  japanese: {
    main: [
      {
        name: 'Whole grain ramen with vegetables',
        description: 'Use whole wheat or brown rice noodles, add extra vegetables, lighter broth. Less refined carbs and sodium.',
        acneImpactScore: 45,
        tags: ['japanese', 'main', 'ramen', 'whole grain', 'vegetables']
      },
      {
        name: 'Zucchini noodles (zoodles) with miso broth',
        description: 'Spiralized zucchini instead of noodles, fermented miso for gut health. Lower in refined carbs.',
        acneImpactScore: 30,
        tags: ['japanese', 'main', 'vegetables', 'fermented', 'lower carb']
      },
      {
        name: 'Brown rice ramen bowl',
        description: 'Brown rice noodles with vegetables and lean protein, less processed than instant ramen.',
        acneImpactScore: 40,
        tags: ['japanese', 'main', 'ramen', 'brown rice', 'whole grain', 'vegetables']
      },
      {
        name: 'Grilled fish with vegetables and brown rice',
        description: 'Grilled fish instead of tempura, served with vegetables and whole grain rice. Lower in refined carbs and oil.',
        acneImpactScore: 35,
        tags: ['japanese', 'main', 'fish', 'grilled', 'vegetables', 'whole grain']
      },
      {
        name: 'Sushi with brown rice and vegetables',
        description: 'Brown rice instead of white, more vegetables, less processed fish. Lower glycemic load.',
        acneImpactScore: 38,
        tags: ['japanese', 'main', 'sushi', 'brown rice', 'whole grain', 'vegetables']
      }
    ],
    side: [
      {
        name: 'Steamed edamame',
        description: 'Steamed soybeans with salt. High in protein and fiber, no frying.',
        acneImpactScore: 25,
        tags: ['japanese', 'side', 'soybeans', 'protein', 'fiberRich', 'steamed']
      },
      {
        name: 'Miso soup with vegetables',
        description: 'Fermented miso broth with vegetables. Supports gut health with probiotics.',
        acneImpactScore: 20,
        tags: ['japanese', 'side', 'fermented', 'probiotics', 'vegetables']
      }
    ],
    snack: [],
    dessert: [],
    drink: [],
    breakfast: []
  },
  
  korean: {
    main: [
      {
        name: 'Grilled bulgogi with vegetables and brown rice',
        description: 'Grilled marinated meat with vegetables, served with brown rice. Less sugar in marinade, less oil than fried versions.',
        acneImpactScore: 42,
        tags: ['korean', 'main', 'grilled', 'lower sugar', 'vegetables', 'whole grain']
      },
      {
        name: 'Bibimbap with extra vegetables and brown rice',
        description: 'Korean rice bowl with more vegetables, less rice, grilled instead of fried. Use brown rice for better glycemic control.',
        acneImpactScore: 35,
        tags: ['korean', 'main', 'rice', 'vegetables', 'whole grain']
      },
      {
        name: 'Kimchi jjigae (fermented vegetable stew)',
        description: 'Fermented kimchi-based stew with vegetables and tofu. Supports gut health with probiotics.',
        acneImpactScore: 30,
        tags: ['korean', 'main', 'fermented', 'probiotics', 'vegetables']
      },
      {
        name: 'Grilled chicken instead of fried',
        description: 'Grilled chicken with Korean spices, served with vegetables and brown rice. Less oil and refined carbs.',
        acneImpactScore: 40,
        tags: ['korean', 'main', 'chicken', 'grilled', 'vegetables', 'whole grain']
      }
    ],
    side: [
      {
        name: 'Kimchi (fermented vegetables)',
        description: 'Probiotic-rich, supports gut health and may reduce inflammation. Traditional Korean side dish.',
        acneImpactScore: 20,
        tags: ['korean', 'side', 'fermented', 'probiotics', 'vegetables']
      },
      {
        name: 'Steamed vegetables with sesame oil',
        description: 'Steamed vegetables with a light drizzle of sesame oil. Lower in oil than fried sides.',
        acneImpactScore: 25,
        tags: ['korean', 'side', 'vegetables', 'steamed', 'healthyFats']
      }
    ],
    snack: [],
    dessert: [],
    drink: [],
    breakfast: []
  },
  
  chinese_american: {
    main: [
      {
        name: 'Baked or air-fried orange chicken',
        description: 'Chicken baked or air-fried instead of deep-fried, with a lighter orange sauce that uses less sugar and cleaner ingredients. Much lower in oil and sugar than classic orange chicken.',
        acneImpactScore: 45,
        tags: ['chinese_american', 'chicken', 'baked', 'airFried', 'lighter_sauce', 'lower sugar']
      },
      {
        name: 'Grilled chicken with lighter orange glaze',
        description: 'Grilled chicken with a homemade orange glaze using minimal sugar and natural orange juice. No deep-frying, lower oil, lower sugar.',
        acneImpactScore: 42,
        tags: ['chinese_american', 'chicken', 'grilled', 'lighter_sauce', 'lower sugar']
      },
      {
        name: 'Baked General Tso\'s chicken',
        description: 'Baked instead of deep-fried, with a lighter sauce using less sugar and corn syrup. Same flavors, much better for skin.',
        acneImpactScore: 48,
        tags: ['chinese_american', 'chicken', 'baked', 'lighter_sauce', 'lower sugar']
      },
      {
        name: 'Baked sweet and sour chicken',
        description: 'Baked chicken with a lighter sweet and sour sauce. Less sugar, no deep-frying, better for acne health.',
        acneImpactScore: 46,
        tags: ['chinese_american', 'chicken', 'baked', 'lighter_sauce', 'lower sugar']
      },
      {
        name: 'Stir-fried chicken with vegetables',
        description: 'Stir-fried chicken with vegetables in a light sauce. Minimal oil, no deep-frying, no heavy glazes.',
        acneImpactScore: 40,
        tags: ['chinese_american', 'chicken', 'stir-fried', 'vegetables', 'lighter_sauce']
      },
      {
        name: 'Steamed chicken with ginger and scallions',
        description: 'Steamed chicken with fresh ginger and scallions. No frying, no heavy sauces, very clean preparation.',
        acneImpactScore: 35,
        tags: ['chinese_american', 'chicken', 'steamed', 'no sauce', 'clean']
      },
      {
        name: 'Baked honey garlic chicken (lighter version)',
        description: 'Baked chicken with a lighter honey garlic sauce using minimal honey. No deep-frying, lower sugar.',
        acneImpactScore: 44,
        tags: ['chinese_american', 'chicken', 'baked', 'lighter_sauce', 'lower sugar']
      },
      {
        name: 'Air-fried chicken with light teriyaki',
        description: 'Air-fried chicken with a homemade teriyaki sauce using less sugar. Much lower oil than deep-fried versions.',
        acneImpactScore: 43,
        tags: ['chinese_american', 'chicken', 'air-fried', 'lighter_sauce', 'lower sugar']
      }
    ],
    side: [],
    snack: [],
    dessert: [],
    drink: [],
    breakfast: []
  },
  
  // Generic dessert alternatives (works across all cuisines)
  generic_dessert: {
    dessert: [
      {
        name: 'Banana Oatmeal 3-Ingredient Cookies',
        description: 'No dairy, no refined sugar, low-glycemic, whole-food ingredients. Soft, chewy oatmeal cookies made with ripe bananas.',
        acneImpactScore: 20,
        tags: ['dessert', 'cookie', 'no dairy', 'lower sugar', 'whole grain', 'fruit']
      },
      {
        name: 'Almond Flour Chocolate Chip Cookies (Dairy-Free)',
        description: 'Almond flour = low glycemic. Uses healthier fats instead of butter. Chewy & soft like regular cookies.',
        acneImpactScore: 30,
        tags: ['dessert', 'cookie', 'dairy-free', 'low glycemic', 'healthyFats', 'almond flour']
      },
      {
        name: 'Apple Cinnamon Soft Cookies',
        description: 'No sugar added, uses fruit for sweetness, anti-inflammatory cinnamon. Made with oats and almond butter.',
        acneImpactScore: 15,
        tags: ['dessert', 'cookie', 'no sugar', 'fruit', 'antiInflammatory', 'whole grain']
      },
      {
        name: 'Greek Yogurt Frozen Cookie Bites (High Protein)',
        description: 'Lower sugar, high protein, frozen = no baking needed. Made with Greek yogurt, almond butter, and dark chocolate chips.',
        acneImpactScore: 35,
        tags: ['dessert', 'cookie', 'high protein', 'lower sugar', 'frozen', 'dairy']
      },
      {
        name: 'Pumpkin Oat Cookies (Anti-Inflammatory)',
        description: 'Pumpkin = Vitamin A (helps skin), oats = low glycemic. Made with pumpkin puree, oats, and maple syrup.',
        acneImpactScore: 18,
        tags: ['dessert', 'cookie', 'antiInflammatory', 'low glycemic', 'whole grain', 'vitaminA']
      },
      {
        name: 'Dark Chocolate Avocado Mousse',
        description: 'Creamy mousse made with avocado and dark chocolate (70%+ cacao). No dairy, lower sugar, healthy fats.',
        acneImpactScore: 28,
        tags: ['dessert', 'mousse', 'dairy-free', 'healthyFats', 'dark chocolate', 'lower sugar']
      },
      {
        name: 'Frozen Banana "Nice Cream"',
        description: 'Blended frozen bananas with a splash of milk for a creamy, dairy-light dessert. No added sugar.',
        acneImpactScore: 25,
        tags: ['dessert', 'ice cream', 'fruit', 'dairy-light', 'no sugar', 'frozen']
      },
      {
        name: 'Date and Nut Energy Balls',
        description: 'Naturally sweetened with dates, packed with nuts for healthy fats and protein. No refined sugar or dairy.',
        acneImpactScore: 22,
        tags: ['dessert', 'snack', 'no sugar', 'healthyFats', 'protein', 'whole foods']
      }
    ],
    main: [],
    side: [],
    snack: [],
    drink: [],
    breakfast: []
  }
};

// Category-specific alternatives (matches food type exactly)
const CATEGORY_ALTERNATIVES: Record<FoodCategory, FoodAlternative[]> = {
  cereal: [
    {
      name: 'Steel-cut oats with berries and nuts',
      description: 'Whole grain oats, high in fiber, topped with fresh berries and healthy fats from nuts. Lower glycemic than processed cereals.',
      acneImpactScore: 25,
      tags: ['breakfast', 'cereal', 'whole grain', 'fiberRich', 'healthyFats', 'berries']
    },
    {
      name: 'Quinoa breakfast bowl with fruit',
      description: 'Protein-rich quinoa instead of processed grains, topped with fresh fruit. Higher protein, lower sugar.',
      acneImpactScore: 22,
      tags: ['breakfast', 'cereal', 'protein', 'whole grain', 'fruit', 'low sugar']
    },
    {
      name: 'Homemade granola with nuts and seeds',
      description: 'Made with whole oats, nuts, seeds, and minimal added sugar. Higher fiber and healthy fats than store-bought.',
      acneImpactScore: 28,
      tags: ['breakfast', 'cereal', 'whole grain', 'healthyFats', 'nuts', 'seeds']
    },
    {
      name: 'Chia seed pudding with berries',
      description: 'High-fiber chia seeds soaked in almond milk, topped with fresh berries. No processed grains, high omega-3.',
      acneImpactScore: 20,
      tags: ['breakfast', 'cereal', 'fiberRich', 'omega3', 'berries', 'dairy-free']
    },
    {
      name: 'Buckwheat porridge with fruit',
      description: 'Gluten-free buckwheat cooked like oatmeal, topped with fresh fruit. Lower glycemic, higher protein than regular cereal.',
      acneImpactScore: 24,
      tags: ['breakfast', 'cereal', 'whole grain', 'protein', 'fruit', 'gluten-free']
    },
    {
      name: 'Millet porridge with nuts',
      description: 'Ancient grain millet cooked into a creamy porridge, topped with nuts. High fiber, lower sugar than processed cereals.',
      acneImpactScore: 23,
      tags: ['breakfast', 'cereal', 'whole grain', 'fiberRich', 'healthyFats', 'nuts']
    },
    {
      name: 'Brown rice porridge with cinnamon',
      description: 'Whole grain brown rice cooked into a warm porridge with cinnamon. Lower glycemic, more fiber than white rice cereal.',
      acneImpactScore: 26,
      tags: ['breakfast', 'cereal', 'whole grain', 'fiberRich', 'antiInflammatory']
    },
    {
      name: 'Amaranth porridge with fruit',
      description: 'Protein-rich amaranth cooked into porridge, topped with fresh fruit. Complete protein, high fiber, no processed grains.',
      acneImpactScore: 21,
      tags: ['breakfast', 'cereal', 'protein', 'whole grain', 'fruit', 'complete protein']
    }
  ],
  
  cake: [
    {
      name: 'Banana bread made with whole wheat flour',
      description: 'Naturally sweetened with ripe bananas, made with whole wheat flour instead of white flour. Lower sugar, higher fiber.',
      acneImpactScore: 32,
      tags: ['dessert', 'cake', 'whole grain', 'fruit', 'lower sugar', 'fiberRich']
    },
    {
      name: 'Carrot cake with almond flour',
      description: 'Made with almond flour instead of white flour, naturally sweetened with carrots and minimal added sugar. Lower glycemic.',
      acneImpactScore: 28,
      tags: ['dessert', 'cake', 'low glycemic', 'healthyFats', 'vegetables', 'almond flour']
    },
    {
      name: 'Zucchini bread with whole grains',
      description: 'Made with whole wheat flour and grated zucchini, naturally moist with less oil. Higher fiber, lower sugar.',
      acneImpactScore: 30,
      tags: ['dessert', 'cake', 'whole grain', 'vegetables', 'fiberRich', 'lower sugar']
    },
    {
      name: 'Pumpkin spice cake with oat flour',
      description: 'Made with oat flour, naturally sweetened with pumpkin and spices. Lower glycemic, high in Vitamin A.',
      acneImpactScore: 26,
      tags: ['dessert', 'cake', 'whole grain', 'vitaminA', 'antiInflammatory', 'low glycemic']
    },
    {
      name: 'Apple cinnamon cake with whole wheat',
      description: 'Made with whole wheat flour, naturally sweetened with apples and cinnamon. No refined sugar, high fiber.',
      acneImpactScore: 24,
      tags: ['dessert', 'cake', 'whole grain', 'fruit', 'antiInflammatory', 'no sugar']
    },
    {
      name: 'Chocolate avocado cake (dairy-free)',
      description: 'Made with avocado for healthy fats and moisture, almond flour, and dark chocolate. Dairy-free, lower sugar.',
      acneImpactScore: 30,
      tags: ['dessert', 'cake', 'dairy-free', 'healthyFats', 'low glycemic', 'dark chocolate']
    },
    {
      name: 'Sweet potato cake with spices',
      description: 'Made with sweet potato puree, whole wheat flour, and spices. Naturally sweet, high in beta-carotene.',
      acneImpactScore: 27,
      tags: ['dessert', 'cake', 'whole grain', 'vitaminA', 'lower sugar', 'vegetables']
    },
    {
      name: 'Date-sweetened spice cake',
      description: 'Naturally sweetened with dates, made with whole grain flour. No refined sugar, high fiber and minerals.',
      acneImpactScore: 25,
      tags: ['dessert', 'cake', 'whole grain', 'no sugar', 'fiberRich', 'dates']
    }
  ],
  
  cookie: [
    {
      name: 'Banana Oatmeal 3-Ingredient Cookies',
      description: 'No dairy, no refined sugar, low-glycemic, whole-food ingredients. Soft, chewy oatmeal cookies made with ripe bananas.',
      acneImpactScore: 20,
      tags: ['dessert', 'cookie', 'no dairy', 'lower sugar', 'whole grain', 'fruit']
    },
    {
      name: 'Almond Flour Chocolate Chip Cookies (Dairy-Free)',
      description: 'Almond flour = low glycemic. Uses healthier fats instead of butter. Chewy & soft like regular cookies.',
      acneImpactScore: 30,
      tags: ['dessert', 'cookie', 'dairy-free', 'low glycemic', 'healthyFats', 'almond flour']
    },
    {
      name: 'Apple Cinnamon Soft Cookies',
      description: 'No sugar added, uses fruit for sweetness, anti-inflammatory cinnamon. Made with oats and almond butter.',
      acneImpactScore: 15,
      tags: ['dessert', 'cookie', 'no sugar', 'fruit', 'antiInflammatory', 'whole grain']
    },
    {
      name: 'Greek Yogurt Frozen Cookie Bites (High Protein)',
      description: 'Lower sugar, high protein, frozen = no baking needed. Made with Greek yogurt, almond butter, and dark chocolate chips.',
      acneImpactScore: 35,
      tags: ['dessert', 'cookie', 'high protein', 'lower sugar', 'frozen', 'dairy']
    },
    {
      name: 'Pumpkin Oat Cookies (Anti-Inflammatory)',
      description: 'Pumpkin = Vitamin A (helps skin), oats = low glycemic. Made with pumpkin puree, oats, and maple syrup.',
      acneImpactScore: 18,
      tags: ['dessert', 'cookie', 'antiInflammatory', 'low glycemic', 'whole grain', 'vitaminA']
    },
    {
      name: 'Date and Nut Cookies',
      description: 'Naturally sweetened with dates, made with nuts and whole grain flour. No refined sugar, high healthy fats.',
      acneImpactScore: 22,
      tags: ['dessert', 'cookie', 'no sugar', 'healthyFats', 'whole grain', 'dates']
    },
    {
      name: 'Oatmeal Raisin Cookies (Whole Grain)',
      description: 'Made with whole grain oats, minimal added sugar, and raisins for natural sweetness. Higher fiber than regular cookies.',
      acneImpactScore: 28,
      tags: ['dessert', 'cookie', 'whole grain', 'fiberRich', 'fruit', 'lower sugar']
    },
    {
      name: 'Coconut Flour Cookies',
      description: 'Made with coconut flour, naturally gluten-free and lower in carbs. Uses healthy fats from coconut.',
      acneImpactScore: 26,
      tags: ['dessert', 'cookie', 'low glycemic', 'healthyFats', 'gluten-free', 'coconut']
    }
  ],
  
  brownies: [
    {
      name: 'Black Bean Brownies (Gluten-Free)',
      description: 'Made with black beans, almond flour, and dark chocolate. High fiber, high protein, no refined flour, lower sugar than traditional brownies.',
      acneImpactScore: 28,
      tags: ['dessert', 'brownies', 'gluten-free', 'high fiber', 'high protein', 'dark chocolate', 'lower sugar']
    },
    {
      name: 'Avocado Chocolate Brownies',
      description: 'Made with avocado for healthy fats and moisture, almond flour, and dark chocolate. Dairy-free, lower sugar, rich in healthy fats.',
      acneImpactScore: 30,
      tags: ['dessert', 'brownies', 'dairy-free', 'healthyFats', 'dark chocolate', 'lower sugar', 'almond flour']
    },
    {
      name: 'Sweet Potato Brownies',
      description: 'Made with sweet potato puree, almond flour, and dark chocolate. Naturally sweet, high in beta-carotene, lower glycemic.',
      acneImpactScore: 26,
      tags: ['dessert', 'brownies', 'vitaminA', 'lower sugar', 'dark chocolate', 'almond flour', 'whole food']
    },
    {
      name: 'Date-Sweetened Brownies',
      description: 'Naturally sweetened with dates, made with almond flour and dark chocolate. No refined sugar, high fiber, rich in minerals.',
      acneImpactScore: 24,
      tags: ['dessert', 'brownies', 'no sugar', 'fiberRich', 'dates', 'dark chocolate', 'almond flour']
    },
    {
      name: 'Zucchini Chocolate Chip Brownies',
      description: 'Made with whole wheat flour, grated zucchini, and dark chocolate chips. Moist, high fiber, lower sugar than traditional brownies.',
      acneImpactScore: 32,
      tags: ['dessert', 'brownies', 'vegetables', 'whole grain', 'fiberRich', 'dark chocolate', 'lower sugar']
    },
    {
      name: 'Almond Flour Brownies (Keto-Friendly)',
      description: 'Made with almond flour, coconut oil, and sugar-free sweetener. Low carb, high healthy fats, no refined sugar.',
      acneImpactScore: 28,
      tags: ['dessert', 'brownies', 'low carb', 'healthyFats', 'no sugar', 'almond flour', 'keto-friendly']
    },
    {
      name: 'Oat Flour Brownies',
      description: 'Made with oat flour, dark chocolate, and minimal added sugar. Whole grain, higher fiber, lower glycemic than white flour brownies.',
      acneImpactScore: 30,
      tags: ['dessert', 'brownies', 'whole grain', 'fiberRich', 'dark chocolate', 'lower sugar', 'oat flour']
    },
    {
      name: 'Coconut Flour Brownies',
      description: 'Made with coconut flour, coconut oil, and dark chocolate. Gluten-free, lower carb, healthy fats from coconut.',
      acneImpactScore: 29,
      tags: ['dessert', 'brownies', 'gluten-free', 'low carb', 'healthyFats', 'dark chocolate', 'coconut']
    }
  ],
  
  ice_cream: [
    {
      name: 'Frozen Banana "Nice Cream"',
      description: 'Blended frozen bananas with a splash of milk for a creamy, dairy-light dessert. No added sugar.',
      acneImpactScore: 25,
      tags: ['dessert', 'ice cream', 'fruit', 'dairy-light', 'no sugar', 'frozen']
    },
    {
      name: 'Coconut Milk Ice Cream',
      description: 'Made with coconut milk instead of dairy, naturally sweetened. Lower in dairy, healthy fats from coconut.',
      acneImpactScore: 28,
      tags: ['dessert', 'ice cream', 'dairy-free', 'healthyFats', 'coconut', 'lower sugar']
    },
    {
      name: 'Greek Yogurt Frozen Treat',
      description: 'Frozen Greek yogurt with berries. Higher protein, lower sugar than regular ice cream.',
      acneImpactScore: 30,
      tags: ['dessert', 'ice cream', 'high protein', 'dairy', 'berries', 'lower sugar']
    },
    {
      name: 'Avocado Chocolate Ice Cream',
      description: 'Creamy ice cream made with avocado and dark chocolate. No dairy, healthy fats, lower sugar.',
      acneImpactScore: 26,
      tags: ['dessert', 'ice cream', 'dairy-free', 'healthyFats', 'dark chocolate', 'lower sugar']
    },
    {
      name: 'Mango Sorbet',
      description: 'Pureed frozen mango, naturally sweet. No dairy, no added sugar, high in Vitamin C.',
      acneImpactScore: 22,
      tags: ['dessert', 'ice cream', 'fruit', 'dairy-free', 'no sugar', 'vitaminC']
    },
    {
      name: 'Almond Milk Ice Cream',
      description: 'Made with almond milk, lower in dairy. Can be sweetened with dates or minimal maple syrup.',
      acneImpactScore: 27,
      tags: ['dessert', 'ice cream', 'dairy-free', 'healthyFats', 'almond', 'lower sugar']
    },
    {
      name: 'Frozen Yogurt with Berries',
      description: 'Greek yogurt frozen with fresh berries. High protein, probiotics, lower sugar than regular ice cream.',
      acneImpactScore: 32,
      tags: ['dessert', 'ice cream', 'high protein', 'probiotic', 'berries', 'dairy']
    },
    {
      name: 'Cashew Milk Ice Cream',
      description: 'Creamy ice cream made with cashew milk. Dairy-free, healthy fats, can be sweetened naturally.',
      acneImpactScore: 29,
      tags: ['dessert', 'ice cream', 'dairy-free', 'healthyFats', 'cashew', 'lower sugar']
    }
  ],
  
  muffin: [
    {
      name: 'Whole Wheat Blueberry Muffins',
      description: 'Made with whole wheat flour, fresh blueberries, and minimal added sugar. Higher fiber, lower glycemic than white flour muffins.',
      acneImpactScore: 28,
      tags: ['breakfast', 'muffin', 'whole grain', 'fiberRich', 'fruit', 'lower sugar']
    },
    {
      name: 'Almond Flour Banana Muffins',
      description: 'Made with almond flour, naturally sweetened with ripe bananas. Gluten-free, lower glycemic, high in healthy fats.',
      acneImpactScore: 26,
      tags: ['breakfast', 'muffin', 'gluten-free', 'low glycemic', 'fruit', 'healthyFats']
    },
    {
      name: 'Oat Flour Carrot Muffins',
      description: 'Made with oat flour, grated carrots, and minimal sugar. High fiber, naturally sweet, anti-inflammatory.',
      acneImpactScore: 24,
      tags: ['breakfast', 'muffin', 'whole grain', 'vegetables', 'fiberRich', 'lower sugar']
    },
    {
      name: 'Zucchini Chocolate Chip Muffins',
      description: 'Made with whole wheat flour, grated zucchini, and dark chocolate chips. Moist, high fiber, lower sugar.',
      acneImpactScore: 30,
      tags: ['breakfast', 'muffin', 'whole grain', 'vegetables', 'dark chocolate', 'lower sugar']
    },
    {
      name: 'Pumpkin Spice Muffins',
      description: 'Made with whole wheat flour, pumpkin puree, and spices. High in Vitamin A, lower sugar, anti-inflammatory.',
      acneImpactScore: 26,
      tags: ['breakfast', 'muffin', 'whole grain', 'vitaminA', 'antiInflammatory', 'lower sugar']
    },
    {
      name: 'Apple Cinnamon Muffins',
      description: 'Made with whole wheat flour, grated apples, and cinnamon. Naturally sweet, high fiber, no refined sugar.',
      acneImpactScore: 22,
      tags: ['breakfast', 'muffin', 'whole grain', 'fruit', 'antiInflammatory', 'no sugar']
    },
    {
      name: 'Sweet Potato Muffins',
      description: 'Made with sweet potato puree, whole wheat flour, and minimal sugar. High in beta-carotene, lower glycemic.',
      acneImpactScore: 25,
      tags: ['breakfast', 'muffin', 'whole grain', 'vitaminA', 'lower sugar', 'vegetables']
    },
    {
      name: 'Date and Walnut Muffins',
      description: 'Naturally sweetened with dates, made with whole wheat flour and walnuts. No refined sugar, high healthy fats.',
      acneImpactScore: 28,
      tags: ['breakfast', 'muffin', 'whole grain', 'no sugar', 'healthyFats', 'dates']
    }
  ],
  
  croissant: [
    {
      name: 'Whole Wheat Croissant (Homemade)',
      description: 'Made with whole wheat flour and minimal butter. Higher fiber, lower refined carbs than traditional croissants.',
      acneImpactScore: 42,
      tags: ['breakfast', 'croissant', 'whole grain', 'fiberRich', 'lower butter']
    },
    {
      name: 'Almond Flour Croissant',
      description: 'Made with almond flour instead of white flour. Gluten-free, lower glycemic, higher healthy fats.',
      acneImpactScore: 38,
      tags: ['breakfast', 'croissant', 'gluten-free', 'low glycemic', 'healthyFats', 'almond flour']
    },
    {
      name: 'Oat Flour Croissant',
      description: 'Made with oat flour and minimal butter. Whole grain, higher fiber, lower glycemic than white flour croissants.',
      acneImpactScore: 40,
      tags: ['breakfast', 'croissant', 'whole grain', 'fiberRich', 'lower butter', 'oat flour']
    },
    {
      name: 'Sweet Potato Croissant',
      description: 'Made with sweet potato puree and whole wheat flour. Naturally sweet, high in beta-carotene, lower butter.',
      acneImpactScore: 36,
      tags: ['breakfast', 'croissant', 'whole grain', 'vitaminA', 'lower butter', 'vegetables']
    },
    {
      name: 'Spinach and Feta Croissant (Whole Wheat)',
      description: 'Whole wheat croissant filled with spinach and light feta. Higher fiber, more nutrients, lower butter.',
      acneImpactScore: 38,
      tags: ['breakfast', 'croissant', 'whole grain', 'vegetables', 'lighter cheese', 'lower butter']
    }
  ],
  
  pizza: [
    {
      name: 'Thin-crust veggie pizza with light cheese',
      description: 'Thinner whole wheat crust, light cheese, and extra vegetables instead of processed meats. Less refined carbs and dairy.',
      acneImpactScore: 45,
      tags: ['main', 'pizza', 'whole grain', 'lighter cheese', 'vegetables', 'lower dairy']
    },
    {
      name: 'Cauliflower crust pizza',
      description: 'Lower-carb, higher-fiber crust alternative with vegetable base. Top with light cheese and vegetables.',
      acneImpactScore: 40,
      tags: ['main', 'pizza', 'low glycemic', 'vegetables', 'fiberRich', 'lighter cheese']
    },
    {
      name: 'Margherita pizza with fresh basil',
      description: 'Simple tomato sauce, light mozzarella, and fresh herbs - less processed than typical pizza with multiple cheeses.',
      acneImpactScore: 48,
      tags: ['main', 'pizza', 'lighter cheese', 'herbs', 'tomato', 'lower dairy']
    },
    {
      name: 'Whole wheat pizza with vegetables',
      description: 'Whole grain crust, minimal cheese, loaded with vegetables. Higher fiber, lower refined carbs.',
      acneImpactScore: 42,
      tags: ['main', 'pizza', 'whole grain', 'vegetables', 'fiberRich', 'lighter cheese']
    },
    {
      name: 'Portobello mushroom pizza',
      description: 'Portobello mushroom cap as the base instead of dough. Top with light cheese and vegetables. Lower carbs.',
      acneImpactScore: 38,
      tags: ['main', 'pizza', 'low glycemic', 'vegetables', 'mushroom', 'lighter cheese']
    },
    {
      name: 'Zucchini crust pizza',
      description: 'Crust made from shredded zucchini and eggs. Lower carbs, higher fiber, topped with light cheese and veggies.',
      acneImpactScore: 35,
      tags: ['main', 'pizza', 'low glycemic', 'vegetables', 'fiberRich', 'lighter cheese']
    },
    {
      name: 'Flatbread pizza with hummus base',
      description: 'Whole grain flatbread with hummus instead of tomato sauce, topped with vegetables. Higher protein, lower dairy.',
      acneImpactScore: 40,
      tags: ['main', 'pizza', 'whole grain', 'protein', 'vegetables', 'lower dairy']
    },
    {
      name: 'Sweet potato crust pizza',
      description: 'Crust made from sweet potato and whole grain flour. Higher fiber, lower refined carbs, topped with veggies.',
      acneImpactScore: 38,
      tags: ['main', 'pizza', 'whole grain', 'vegetables', 'fiberRich', 'vitaminA']
    }
  ],
  
  burger: [
    {
      name: 'Grilled chicken or turkey burger on whole-grain bun',
      description: 'Leaner protein, whole grain bun, and side salad instead of fries. Less processed meat and refined carbs.',
      acneImpactScore: 45,
      tags: ['main', 'burger', 'whole grain', 'lean protein', 'vegetables', 'grilled']
    },
    {
      name: 'Veggie burger with avocado',
      description: 'Plant-based patty with healthy fats from avocado, whole grain bun. Less processed than beef burgers.',
      acneImpactScore: 40,
      tags: ['main', 'burger', 'vegetables', 'healthyFats', 'whole grain', 'plant-based']
    },
    {
      name: 'Salmon burger on whole grain bun',
      description: 'Grilled salmon patty with omega-3s, whole grain bun, and vegetables. Healthy fats, lower processed meat.',
      acneImpactScore: 42,
      tags: ['main', 'burger', 'omega3', 'whole grain', 'protein', 'healthyFats']
    },
    {
      name: 'Black bean burger with whole grain bun',
      description: 'Plant-based patty made from black beans, whole grain bun. High fiber, high protein, no processed meat.',
      acneImpactScore: 38,
      tags: ['main', 'burger', 'beans', 'fiberRich', 'whole grain', 'plant-based']
    },
    {
      name: 'Portobello mushroom burger',
      description: 'Grilled portobello mushroom cap as the patty, whole grain bun. Lower carbs, high fiber, plant-based.',
      acneImpactScore: 35,
      tags: ['main', 'burger', 'vegetables', 'whole grain', 'fiberRich', 'plant-based']
    },
    {
      name: 'Lentil burger with whole grain bun',
      description: 'Plant-based patty made from lentils, whole grain bun. High protein, high fiber, no processed meat.',
      acneImpactScore: 36,
      tags: ['main', 'burger', 'lentils', 'protein', 'whole grain', 'plant-based']
    },
    {
      name: 'Chickpea burger with whole grain bun',
      description: 'Plant-based patty made from chickpeas, whole grain bun. High protein, high fiber, lower processed ingredients.',
      acneImpactScore: 37,
      tags: ['main', 'burger', 'chickpeas', 'protein', 'whole grain', 'plant-based']
    },
    {
      name: 'Quinoa burger with whole grain bun',
      description: 'Plant-based patty made from quinoa, whole grain bun. Complete protein, high fiber, no processed meat.',
      acneImpactScore: 39,
      tags: ['main', 'burger', 'quinoa', 'complete protein', 'whole grain', 'plant-based']
    }
  ],
  
  pasta: [
    {
      name: 'Whole-wheat spaghetti with olive oil, garlic, and vegetables',
      description: 'Complex carbs from whole grains, healthy fats from olive oil, and fiber from vegetables. No heavy cream or excessive cheese.',
      acneImpactScore: 35,
      tags: ['main', 'pasta', 'whole grain', 'healthyFats', 'vegetables', 'fiberRich']
    },
    {
      name: 'Zucchini noodles (zoodles) with marinara',
      description: 'Spiralized zucchini instead of pasta, topped with tomato sauce and light cheese. Lower in refined carbs.',
      acneImpactScore: 30,
      tags: ['main', 'pasta', 'low glycemic', 'vegetables', 'fiberRich', 'lighter cheese']
    },
    {
      name: 'Tomato-based pasta with veggies & light cheese',
      description: 'Marinara or pomodoro sauce instead of creamy alfredo. Add vegetables and use minimal cheese for flavor. Lower in dairy and saturated fat.',
      acneImpactScore: 45,
      tags: ['main', 'pasta', 'tomato', 'vegetables', 'lighter cheese', 'lower dairy']
    },
    {
      name: 'Spaghetti squash with marinara',
      description: 'Spaghetti squash instead of pasta, topped with tomato sauce. Lower carbs, higher fiber, no refined grains.',
      acneImpactScore: 28,
      tags: ['main', 'pasta', 'low glycemic', 'vegetables', 'fiberRich', 'no refined carbs']
    },
    {
      name: 'Lentil pasta with vegetables',
      description: 'Pasta made from lentils instead of wheat. Higher protein, higher fiber, lower glycemic impact.',
      acneImpactScore: 32,
      tags: ['main', 'pasta', 'protein', 'fiberRich', 'vegetables', 'low glycemic']
    },
    {
      name: 'Chickpea pasta with olive oil and vegetables',
      description: 'Pasta made from chickpeas, tossed with olive oil and vegetables. Higher protein, higher fiber, lower carbs.',
      acneImpactScore: 30,
      tags: ['main', 'pasta', 'protein', 'fiberRich', 'healthyFats', 'vegetables']
    },
    {
      name: 'Brown rice pasta with vegetables',
      description: 'Pasta made from brown rice, whole grain alternative. Higher fiber than white pasta, topped with vegetables.',
      acneImpactScore: 38,
      tags: ['main', 'pasta', 'whole grain', 'fiberRich', 'vegetables', 'gluten-free']
    },
    {
      name: 'Quinoa pasta with vegetables',
      description: 'Pasta made from quinoa, complete protein source. Higher protein, higher fiber, lower glycemic than regular pasta.',
      acneImpactScore: 33,
      tags: ['main', 'pasta', 'complete protein', 'fiberRich', 'vegetables', 'low glycemic']
    }
  ],
  
  fries: [
    {
      name: 'Oven-baked potato wedges',
      description: 'Baked with a little olive oil instead of deep frying to reduce oil load.',
      acneImpactScore: 45,
      tags: ['side', 'fries', 'baked', 'lower oil', 'potato']
    },
    {
      name: 'Sweet potato fries (baked)',
      description: 'Baked sweet potato fries with minimal oil. Lower glycemic than white potatoes, more nutrients.',
      acneImpactScore: 35,
      tags: ['side', 'fries', 'baked', 'lower oil', 'sweet potato', 'vitaminA']
    },
    {
      name: 'Carrot fries (baked)',
      description: 'Baked carrot sticks instead of potato fries. Lower carbs, higher fiber, high in beta-carotene.',
      acneImpactScore: 25,
      tags: ['side', 'fries', 'baked', 'vegetables', 'fiberRich', 'vitaminA']
    },
    {
      name: 'Zucchini fries (baked)',
      description: 'Baked zucchini sticks instead of potato fries. Lower carbs, higher fiber, lower glycemic.',
      acneImpactScore: 28,
      tags: ['side', 'fries', 'baked', 'vegetables', 'fiberRich', 'low glycemic']
    },
    {
      name: 'Air-fried potato wedges',
      description: 'Potato wedges cooked in an air fryer with minimal oil. Same crispy texture with 70-80% less oil.',
      acneImpactScore: 42,
      tags: ['side', 'fries', 'air-fried', 'lower oil', 'potato']
    },
    {
      name: 'Parsnip fries (baked)',
      description: 'Baked parsnip sticks instead of potato fries. Lower glycemic, higher fiber, more nutrients.',
      acneImpactScore: 30,
      tags: ['side', 'fries', 'baked', 'vegetables', 'fiberRich', 'low glycemic']
    },
    {
      name: 'Jicama fries (baked)',
      description: 'Baked jicama sticks instead of potato fries. Very low carbs, high fiber, crunchy texture.',
      acneImpactScore: 22,
      tags: ['side', 'fries', 'baked', 'vegetables', 'fiberRich', 'very low glycemic']
    },
    {
      name: 'Turnip fries (baked)',
      description: 'Baked turnip sticks instead of potato fries. Lower carbs, higher fiber, more nutrients than white potatoes.',
      acneImpactScore: 32,
      tags: ['side', 'fries', 'baked', 'vegetables', 'fiberRich', 'low glycemic']
    }
  ],
  
  chips: [
    {
      name: 'Baked tortilla chips with salsa',
      description: 'Baked instead of fried chips, served with fresh salsa. Lower in oil and processed fats.',
      acneImpactScore: 35,
      tags: ['snack', 'chips', 'baked', 'lower oil', 'salsa']
    },
    {
      name: 'Kale chips (baked)',
      description: 'Baked kale leaves instead of potato chips. High in vitamins, fiber, and antioxidants. Very low in calories.',
      acneImpactScore: 15,
      tags: ['snack', 'chips', 'baked', 'vegetables', 'fiberRich', 'antioxidants']
    },
    {
      name: 'Roasted chickpeas',
      description: 'High-fiber, protein-rich alternative to chips with healthy spices. Baked instead of fried.',
      acneImpactScore: 25,
      tags: ['snack', 'chips', 'baked', 'protein', 'fiberRich', 'beans']
    },
    {
      name: 'Baked sweet potato chips',
      description: 'Thinly sliced sweet potatoes baked with minimal oil. Lower glycemic than white potatoes, more nutrients.',
      acneImpactScore: 32,
      tags: ['snack', 'chips', 'baked', 'sweet potato', 'vitaminA', 'lower oil']
    },
    {
      name: 'Air-popped popcorn with olive oil',
      description: 'Whole-grain, lower oil alternative to greasy chips. High fiber, minimal processing.',
      acneImpactScore: 30,
      tags: ['snack', 'chips', 'whole grain', 'fiberRich', 'lower oil', 'popcorn']
    },
    {
      name: 'Baked zucchini chips',
      description: 'Thinly sliced zucchini baked until crispy. Lower carbs, higher fiber than potato chips.',
      acneImpactScore: 20,
      tags: ['snack', 'chips', 'baked', 'vegetables', 'fiberRich', 'low glycemic']
    },
    {
      name: 'Roasted edamame',
      description: 'Roasted edamame beans instead of chips. High protein, high fiber, healthy snack.',
      acneImpactScore: 28,
      tags: ['snack', 'chips', 'baked', 'protein', 'fiberRich', 'soy']
    },
    {
      name: 'Baked beet chips',
      description: 'Thinly sliced beets baked until crispy. High in antioxidants, fiber, and nutrients.',
      acneImpactScore: 24,
      tags: ['snack', 'chips', 'baked', 'vegetables', 'fiberRich', 'antioxidants']
    }
  ],
  
  soda: [
    {
      name: 'Sparkling water with lemon or berries',
      description: 'Zero-sugar alternative that still feels like a treat and is more skin-friendly.',
      acneImpactScore: 10,
      tags: ['drink', 'soda', 'no sugar', 'fruit', 'zero calories']
    },
    {
      name: 'Herbal iced tea (unsweetened)',
      description: 'Naturally flavored herbal tea, served cold. No added sugar, antioxidants from herbs.',
      acneImpactScore: 8,
      tags: ['drink', 'soda', 'no sugar', 'herbs', 'antioxidants', 'tea']
    },
    {
      name: 'Kombucha (low sugar)',
      description: 'Fermented tea with probiotics. Choose low-sugar varieties for gut health benefits.',
      acneImpactScore: 25,
      tags: ['drink', 'soda', 'fermented', 'probiotic', 'lower sugar', 'tea']
    },
    {
      name: 'Coconut water (unsweetened)',
      description: 'Natural electrolytes, minimal sugar. Refreshing alternative to soda.',
      acneImpactScore: 20,
      tags: ['drink', 'soda', 'natural', 'electrolytes', 'lower sugar', 'coconut']
    },
    {
      name: 'Green tea (iced, unsweetened)',
      description: 'Antioxidant-rich green tea served cold. No added sugar, supports skin health.',
      acneImpactScore: 5,
      tags: ['drink', 'soda', 'no sugar', 'antioxidants', 'tea', 'skin-friendly']
    },
    {
      name: 'Infused water with cucumber and mint',
      description: 'Water infused with fresh cucumber and mint. Zero calories, refreshing, no sugar.',
      acneImpactScore: 5,
      tags: ['drink', 'soda', 'no sugar', 'zero calories', 'herbs', 'water']
    },
    {
      name: 'Sparkling water with a splash of fruit juice',
      description: 'Sparkling water with just a splash of 100% fruit juice. Much lower sugar than soda.',
      acneImpactScore: 18,
      tags: ['drink', 'soda', 'lower sugar', 'fruit', 'sparkling']
    },
    {
      name: 'Homemade lemonade (minimal sugar)',
      description: 'Fresh lemon juice with minimal honey or maple syrup. Much lower sugar than store-bought.',
      acneImpactScore: 22,
      tags: ['drink', 'soda', 'lower sugar', 'fruit', 'natural', 'homemade']
    }
  ],
  
  pancake: [
    {
      name: '3-Ingredient Banana Oat Pancakes',
      description: 'Mashed ripe bananas, oat flour, and milk cooked on a non-stick pan for a lower-sugar, higher-fiber pancake.',
      acneImpactScore: 35,
      tags: ['breakfast', 'pancake', 'lower sugar', 'whole grain', 'fruit']
    },
    {
      name: 'Whole grain pancakes with berries',
      description: 'Use whole wheat or oat flour, minimal sugar, and top with fresh berries instead of syrup.',
      acneImpactScore: 40,
      tags: ['breakfast', 'pancake', 'whole grain', 'berries', 'lower sugar']
    },
    {
      name: 'Almond flour pancakes',
      description: 'Made with almond flour instead of white flour. Lower glycemic, higher protein, healthy fats.',
      acneImpactScore: 32,
      tags: ['breakfast', 'pancake', 'low glycemic', 'healthyFats', 'almond flour']
    },
    {
      name: 'Buckwheat pancakes',
      description: 'Made with buckwheat flour, naturally gluten-free. Lower glycemic, higher protein than white flour pancakes.',
      acneImpactScore: 30,
      tags: ['breakfast', 'pancake', 'low glycemic', 'protein', 'gluten-free']
    },
    {
      name: 'Sweet potato pancakes',
      description: 'Made with sweet potato puree and whole grain flour. Higher fiber, lower sugar, naturally sweet.',
      acneImpactScore: 28,
      tags: ['breakfast', 'pancake', 'whole grain', 'vitaminA', 'lower sugar']
    },
    {
      name: 'Coconut flour pancakes',
      description: 'Made with coconut flour, lower in carbs. Uses healthy fats from coconut, naturally gluten-free.',
      acneImpactScore: 34,
      tags: ['breakfast', 'pancake', 'low glycemic', 'healthyFats', 'coconut', 'gluten-free']
    },
    {
      name: 'Protein pancakes with Greek yogurt',
      description: 'Made with protein powder or Greek yogurt, whole grain flour. Higher protein, lower sugar.',
      acneImpactScore: 38,
      tags: ['breakfast', 'pancake', 'high protein', 'whole grain', 'lower sugar']
    },
    {
      name: 'Chia seed pancakes',
      description: 'Made with chia seeds and whole grain flour. High fiber, omega-3s, lower glycemic.',
      acneImpactScore: 31,
      tags: ['breakfast', 'pancake', 'fiberRich', 'omega3', 'whole grain']
    }
  ],
  
  waffle: [
    {
      name: 'Whole grain waffles with berries',
      description: 'Made with whole wheat or oat flour, minimal sugar, topped with fresh berries instead of syrup.',
      acneImpactScore: 42,
      tags: ['breakfast', 'waffle', 'whole grain', 'berries', 'lower sugar']
    },
    {
      name: 'Almond flour waffles',
      description: 'Made with almond flour instead of white flour. Lower glycemic, higher protein, healthy fats.',
      acneImpactScore: 35,
      tags: ['breakfast', 'waffle', 'low glycemic', 'healthyFats', 'almond flour']
    },
    {
      name: 'Buckwheat waffles',
      description: 'Made with buckwheat flour, naturally gluten-free. Lower glycemic, higher protein.',
      acneImpactScore: 33,
      tags: ['breakfast', 'waffle', 'low glycemic', 'protein', 'gluten-free']
    },
    {
      name: 'Sweet potato waffles',
      description: 'Made with sweet potato puree and whole grain flour. Higher fiber, lower sugar, naturally sweet.',
      acneImpactScore: 31,
      tags: ['breakfast', 'waffle', 'whole grain', 'vitaminA', 'lower sugar']
    },
    {
      name: 'Protein waffles with Greek yogurt',
      description: 'Made with protein powder or Greek yogurt, whole grain flour. Higher protein, lower sugar.',
      acneImpactScore: 40,
      tags: ['breakfast', 'waffle', 'high protein', 'whole grain', 'lower sugar']
    },
    {
      name: 'Coconut flour waffles',
      description: 'Made with coconut flour, lower in carbs. Uses healthy fats from coconut, naturally gluten-free.',
      acneImpactScore: 36,
      tags: ['breakfast', 'waffle', 'low glycemic', 'healthyFats', 'coconut', 'gluten-free']
    },
    {
      name: 'Oat flour waffles',
      description: 'Made with oat flour, whole grain alternative. Higher fiber, lower glycemic than white flour.',
      acneImpactScore: 38,
      tags: ['breakfast', 'waffle', 'whole grain', 'fiberRich', 'low glycemic']
    },
    {
      name: 'Chia seed waffles',
      description: 'Made with chia seeds and whole grain flour. High fiber, omega-3s, lower glycemic.',
      acneImpactScore: 34,
      tags: ['breakfast', 'waffle', 'fiberRich', 'omega3', 'whole grain']
    }
  ],
  
  donut: [
    {
      name: 'Baked donut holes (whole grain)',
      description: 'Made with whole grain flour, baked instead of fried. Lower oil, higher fiber, minimal sugar.',
      acneImpactScore: 38,
      tags: ['dessert', 'donut', 'whole grain', 'baked', 'lower oil', 'lower sugar']
    },
    {
      name: 'Almond flour donuts (baked)',
      description: 'Made with almond flour, baked instead of fried. Lower glycemic, healthy fats, no refined flour.',
      acneImpactScore: 32,
      tags: ['dessert', 'donut', 'low glycemic', 'healthyFats', 'almond flour', 'baked']
    },
    {
      name: 'Sweet potato donut holes (baked)',
      description: 'Made with sweet potato puree and whole grain flour, baked. Higher fiber, lower sugar, naturally sweet.',
      acneImpactScore: 30,
      tags: ['dessert', 'donut', 'whole grain', 'vitaminA', 'baked', 'lower sugar']
    },
    {
      name: 'Protein donut holes (baked)',
      description: 'Made with protein powder and whole grain flour, baked. Higher protein, lower sugar, no frying.',
      acneImpactScore: 35,
      tags: ['dessert', 'donut', 'high protein', 'whole grain', 'baked', 'lower sugar']
    },
    {
      name: 'Oat flour donuts (baked)',
      description: 'Made with oat flour, baked instead of fried. Higher fiber, lower glycemic, minimal oil.',
      acneImpactScore: 33,
      tags: ['dessert', 'donut', 'whole grain', 'fiberRich', 'baked', 'lower oil']
    },
    {
      name: 'Coconut flour donuts (baked)',
      description: 'Made with coconut flour, baked. Lower carbs, healthy fats, naturally gluten-free.',
      acneImpactScore: 34,
      tags: ['dessert', 'donut', 'low glycemic', 'healthyFats', 'coconut', 'baked']
    },
    {
      name: 'Date-sweetened donut holes (baked)',
      description: 'Naturally sweetened with dates, made with whole grain flour, baked. No refined sugar, high fiber.',
      acneImpactScore: 28,
      tags: ['dessert', 'donut', 'no sugar', 'whole grain', 'fiberRich', 'baked']
    },
    {
      name: 'Pumpkin spice donut holes (baked)',
      description: 'Made with pumpkin puree and whole grain flour, baked. High in Vitamin A, lower sugar.',
      acneImpactScore: 29,
      tags: ['dessert', 'donut', 'whole grain', 'vitaminA', 'baked', 'lower sugar']
    }
  ],
  
  pie: [
    {
      name: 'Crustless fruit pie',
      description: 'Baked fruit filling without the refined flour crust. Lower carbs, higher fiber, naturally sweet.',
      acneImpactScore: 28,
      tags: ['dessert', 'pie', 'fruit', 'no refined carbs', 'fiberRich', 'lower sugar']
    },
    {
      name: 'Whole grain fruit pie',
      description: 'Made with whole wheat or oat flour crust, naturally sweetened fruit filling. Higher fiber, lower sugar.',
      acneImpactScore: 35,
      tags: ['dessert', 'pie', 'whole grain', 'fruit', 'fiberRich', 'lower sugar']
    },
    {
      name: 'Almond flour pie crust with fruit',
      description: 'Crust made from almond flour, filled with fresh fruit. Lower glycemic, healthy fats, no refined flour.',
      acneImpactScore: 32,
      tags: ['dessert', 'pie', 'low glycemic', 'healthyFats', 'fruit', 'almond flour']
    },
    {
      name: 'Pumpkin pie with whole grain crust',
      description: 'Made with whole grain crust, naturally sweetened pumpkin filling. High in Vitamin A, lower sugar.',
      acneImpactScore: 30,
      tags: ['dessert', 'pie', 'whole grain', 'vitaminA', 'lower sugar', 'pumpkin']
    },
    {
      name: 'Apple pie with oat flour crust',
      description: 'Crust made from oat flour, filled with naturally sweetened apples. Higher fiber, lower sugar.',
      acneImpactScore: 33,
      tags: ['dessert', 'pie', 'whole grain', 'fruit', 'fiberRich', 'lower sugar']
    },
    {
      name: 'Berry crumble (no bottom crust)',
      description: 'Baked berries with a whole grain crumble topping, no bottom crust. Lower carbs, higher fiber.',
      acneImpactScore: 27,
      tags: ['dessert', 'pie', 'whole grain', 'berries', 'fiberRich', 'lower carbs']
    },
    {
      name: 'Sweet potato pie with whole grain crust',
      description: 'Made with whole grain crust, naturally sweetened sweet potato filling. High in beta-carotene, lower sugar.',
      acneImpactScore: 31,
      tags: ['dessert', 'pie', 'whole grain', 'vitaminA', 'lower sugar', 'sweet potato']
    },
    {
      name: 'Date-sweetened fruit pie',
      description: 'Naturally sweetened with dates, made with whole grain crust. No refined sugar, high fiber.',
      acneImpactScore: 26,
      tags: ['dessert', 'pie', 'no sugar', 'whole grain', 'fiberRich', 'fruit']
    }
  ],
  
  chocolate: [
    {
      name: 'Dark chocolate 70%+ cacao',
      description: 'Less sugar and more cocoa compared to milk chocolate. Higher antioxidants, lower sugar.',
      acneImpactScore: 40,
      tags: ['dessert', 'chocolate', 'dark chocolate', 'lower sugar', 'antioxidants']
    },
    {
      name: 'Cacao nibs with nuts',
      description: 'Raw cacao nibs mixed with nuts. No added sugar, high antioxidants, healthy fats.',
      acneImpactScore: 25,
      tags: ['dessert', 'chocolate', 'no sugar', 'antioxidants', 'healthyFats', 'nuts']
    },
    {
      name: 'Dark chocolate avocado mousse',
      description: 'Creamy mousse made with avocado and dark chocolate (70%+ cacao). No dairy, lower sugar, healthy fats.',
      acneImpactScore: 28,
      tags: ['dessert', 'chocolate', 'dairy-free', 'healthyFats', 'dark chocolate', 'lower sugar']
    },
    {
      name: 'Cacao powder with almond milk',
      description: 'Unsweetened cacao powder mixed with almond milk. No added sugar, high antioxidants, dairy-free.',
      acneImpactScore: 20,
      tags: ['dessert', 'chocolate', 'no sugar', 'antioxidants', 'dairy-free', 'cacao']
    },
    {
      name: 'Dark chocolate covered nuts',
      description: 'Nuts covered in dark chocolate (70%+). Healthy fats, antioxidants, lower sugar than milk chocolate.',
      acneImpactScore: 32,
      tags: ['dessert', 'chocolate', 'healthyFats', 'antioxidants', 'dark chocolate', 'nuts']
    },
    {
      name: 'Raw cacao smoothie',
      description: 'Smoothie made with raw cacao powder, banana, and almond milk. No added sugar, high antioxidants.',
      acneImpactScore: 22,
      tags: ['dessert', 'chocolate', 'no sugar', 'antioxidants', 'fruit', 'dairy-free']
    },
    {
      name: 'Dark chocolate with sea salt',
      description: 'Dark chocolate (70%+) with a sprinkle of sea salt. Lower sugar, antioxidants, minimal processing.',
      acneImpactScore: 38,
      tags: ['dessert', 'chocolate', 'dark chocolate', 'lower sugar', 'antioxidants']
    },
    {
      name: 'Cacao energy balls',
      description: 'Date and nut balls with cacao powder. No added sugar, high fiber, antioxidants, healthy fats.',
      acneImpactScore: 24,
      tags: ['dessert', 'chocolate', 'no sugar', 'fiberRich', 'antioxidants', 'healthyFats']
    }
  ],
  
  candy: [
    {
      name: 'Fresh fruit (berries, grapes, mango)',
      description: 'Naturally sweet fruit instead of processed candy. High fiber, vitamins, antioxidants, no added sugar.',
      acneImpactScore: 15,
      tags: ['dessert', 'candy', 'fruit', 'no sugar', 'fiberRich', 'antioxidants']
    },
    {
      name: 'Frozen grapes',
      description: 'Frozen grapes for a sweet, cold treat. No added sugar, high antioxidants, naturally sweet.',
      acneImpactScore: 18,
      tags: ['dessert', 'candy', 'fruit', 'no sugar', 'antioxidants', 'frozen']
    },
    {
      name: 'Date and nut energy balls',
      description: 'Naturally sweetened with dates, packed with nuts. No refined sugar, healthy fats, high fiber.',
      acneImpactScore: 22,
      tags: ['dessert', 'candy', 'no sugar', 'healthyFats', 'fiberRich', 'dates']
    },
    {
      name: 'Dark chocolate covered fruit',
      description: 'Fresh fruit (strawberries, bananas) covered in dark chocolate (70%+). Lower sugar, antioxidants.',
      acneImpactScore: 28,
      tags: ['dessert', 'candy', 'fruit', 'dark chocolate', 'lower sugar', 'antioxidants']
    },
    {
      name: 'Homemade fruit leather',
      description: 'Dehydrated fruit puree, no added sugar. High fiber, vitamins, naturally sweet.',
      acneImpactScore: 20,
      tags: ['dessert', 'candy', 'fruit', 'no sugar', 'fiberRich', 'vitamins']
    },
    {
      name: 'Cacao nibs',
      description: 'Raw cacao nibs, no added sugar. High antioxidants, fiber, natural chocolate flavor.',
      acneImpactScore: 25,
      tags: ['dessert', 'candy', 'no sugar', 'antioxidants', 'fiberRich', 'cacao']
    },
    {
      name: 'Frozen banana bites',
      description: 'Frozen banana slices, optionally dipped in dark chocolate. No added sugar, naturally sweet.',
      acneImpactScore: 22,
      tags: ['dessert', 'candy', 'fruit', 'no sugar', 'frozen', 'naturally sweet']
    },
    {
      name: 'Dried fruit (no added sugar)',
      description: 'Naturally dried fruit like dates, figs, apricots. High fiber, vitamins, no added sugar.',
      acneImpactScore: 24,
      tags: ['dessert', 'candy', 'fruit', 'no sugar', 'fiberRich', 'vitamins']
    }
  ],
  
  bread: [
    {
      name: 'Whole grain bread',
      description: 'Made with whole wheat, oats, or other whole grains. Higher fiber, lower glycemic than white bread.',
      acneImpactScore: 35,
      tags: ['main', 'bread', 'whole grain', 'fiberRich', 'lower glycemic']
    },
    {
      name: 'Sprouted grain bread',
      description: 'Made from sprouted grains, easier to digest. Higher protein, higher fiber, lower glycemic.',
      acneImpactScore: 30,
      tags: ['main', 'bread', 'whole grain', 'protein', 'fiberRich', 'sprouted']
    },
    {
      name: 'Sourdough bread (whole grain)',
      description: 'Fermented sourdough made with whole grains. Lower glycemic, easier to digest, probiotics.',
      acneImpactScore: 32,
      tags: ['main', 'bread', 'whole grain', 'fermented', 'probiotic', 'lower glycemic']
    },
    {
      name: 'Rye bread',
      description: 'Made with rye flour, higher fiber than white bread. Lower glycemic, more nutrients.',
      acneImpactScore: 33,
      tags: ['main', 'bread', 'whole grain', 'fiberRich', 'lower glycemic', 'rye']
    },
    {
      name: 'Oat bread',
      description: 'Made with oat flour, whole grain alternative. Higher fiber, lower glycemic than white bread.',
      acneImpactScore: 31,
      tags: ['main', 'bread', 'whole grain', 'fiberRich', 'lower glycemic', 'oats']
    },
    {
      name: 'Almond flour bread',
      description: 'Made with almond flour, lower in carbs. Higher protein, healthy fats, lower glycemic.',
      acneImpactScore: 28,
      tags: ['main', 'bread', 'low glycemic', 'healthyFats', 'protein', 'almond flour']
    },
    {
      name: 'Quinoa bread',
      description: 'Made with quinoa flour, complete protein source. Higher protein, higher fiber, lower glycemic.',
      acneImpactScore: 29,
      tags: ['main', 'bread', 'complete protein', 'fiberRich', 'lower glycemic', 'quinoa']
    },
    {
      name: 'Seed bread',
      description: 'Made with seeds (flax, chia, sunflower), high in omega-3s and fiber. Lower carbs, healthy fats.',
      acneImpactScore: 27,
      tags: ['main', 'bread', 'omega3', 'fiberRich', 'healthyFats', 'seeds']
    }
  ],
  
  other: []
};

// Healthy snack brand recommendations organized by category
const HEALTHY_BRANDS_BY_CATEGORY: Record<string, HealthyBrand[]> = {
  chips: [
    {
      name: "Jackson's Honest Sweet Potato Chips (Avocado Oil)",
      description: 'Made with avocado oil, sweet potatoes, and sea salt. No artificial ingredients, lower glycemic than white potatoes.',
      acneImpactScore: 28,
      whyHealthy: 'Avocado oil provides healthy monounsaturated fats. Sweet potatoes have lower glycemic index than white potatoes, reducing blood sugar spikes that can trigger acne.',
      tags: ['chips', 'sweet potato', 'avocado oil', 'no artificial', 'lower glycemic']
    },
    {
      name: 'Siete Grain Free Tortilla Chips',
      description: 'Made with cassava flour and avocado oil. Gluten-free, grain-free, no artificial ingredients.',
      acneImpactScore: 30,
      whyHealthy: 'Cassava flour is lower glycemic than corn or wheat. Avocado oil instead of processed vegetable oils reduces inflammation.',
      tags: ['chips', 'grain-free', 'avocado oil', 'gluten-free', 'no artificial']
    },
    {
      name: 'Boulder Canyon Kettle Cooked Chips (Olive Oil)',
      description: 'Kettle-cooked chips made with olive oil and sea salt. No trans fats, no artificial flavors.',
      acneImpactScore: 32,
      whyHealthy: 'Olive oil is rich in anti-inflammatory compounds. Kettle cooking uses less oil than deep-frying.',
      tags: ['chips', 'olive oil', 'kettle-cooked', 'no trans fats', 'no artificial']
    },
    {
      name: 'Terra Original Chips (Root Vegetables)',
      description: 'Made from root vegetables (sweet potato, taro, parsnip) with canola oil. Lower glycemic than potato chips.',
      acneImpactScore: 35,
      whyHealthy: 'Root vegetables provide more fiber and nutrients than white potatoes. Variety of vegetables increases antioxidant intake.',
      tags: ['chips', 'root vegetables', 'fiberRich', 'lower glycemic', 'antioxidants']
    },
    {
      name: 'Late July Organic Tortilla Chips',
      description: 'Organic corn, sunflower oil, and sea salt. Non-GMO, no artificial preservatives.',
      acneImpactScore: 38,
      whyHealthy: 'Organic ingredients reduce pesticide exposure. Sunflower oil is less processed than hydrogenated oils.',
      tags: ['chips', 'organic', 'non-GMO', 'sunflower oil', 'no artificial']
    }
  ],
  
  cookies: [
    {
      name: 'Simple Mills Almond Flour Cookies',
      description: 'Made with almond flour, coconut sugar, and coconut oil. Grain-free, no refined sugar, no artificial ingredients.',
      acneImpactScore: 32,
      whyHealthy: 'Almond flour is low glycemic and provides healthy fats. Coconut sugar has lower glycemic index than white sugar.',
      tags: ['cookies', 'almond flour', 'grain-free', 'no refined sugar', 'coconut oil']
    },
    {
      name: 'Partake Foods Cookies',
      description: 'Made with whole grain oats, coconut oil, and minimal added sugar. Free from top 9 allergens, no artificial flavors.',
      acneImpactScore: 35,
      whyHealthy: 'Whole grain oats provide fiber and have lower glycemic impact. Coconut oil instead of butter reduces saturated fat.',
      tags: ['cookies', 'whole grain', 'oats', 'coconut oil', 'lower sugar', 'allergen-free']
    },
    {
      name: 'Hu Kitchen Simple Cookies',
      description: 'Made with almond flour, coconut sugar, and coconut oil. Paleo-friendly, no refined sugar, no artificial ingredients.',
      acneImpactScore: 30,
      whyHealthy: 'Almond flour is low glycemic. Coconut sugar provides minerals and has lower glycemic index than white sugar.',
      tags: ['cookies', 'almond flour', 'paleo', 'coconut sugar', 'no refined sugar']
    },
    {
      name: 'Lakanto Sugar-Free Cookies',
      description: 'Made with monk fruit sweetener, almond flour, and coconut oil. Zero sugar, low glycemic, keto-friendly.',
      acneImpactScore: 25,
      whyHealthy: 'Monk fruit sweetener has zero glycemic impact, preventing blood sugar spikes. Almond flour provides healthy fats.',
      tags: ['cookies', 'zero sugar', 'monk fruit', 'low glycemic', 'almond flour']
    }
  ],
  
  brownies: [
    {
      name: 'Simple Mills Almond Flour Brownies',
      description: 'Made with almond flour, coconut sugar, and cacao. Grain-free, no refined sugar, no artificial ingredients.',
      acneImpactScore: 30,
      whyHealthy: 'Almond flour is low glycemic. Coconut sugar has lower glycemic index than white sugar. Cacao provides antioxidants.',
      tags: ['brownies', 'almond flour', 'grain-free', 'coconut sugar', 'cacao', 'antioxidants']
    },
    {
      name: 'Hu Kitchen Dark Chocolate Brownies',
      description: 'Made with almond flour, coconut sugar, and 70%+ dark chocolate. Paleo-friendly, no refined sugar.',
      acneImpactScore: 28,
      whyHealthy: 'Dark chocolate (70%+) has less sugar and more antioxidants. Almond flour provides healthy fats and low glycemic carbs.',
      tags: ['brownies', 'dark chocolate', 'almond flour', 'coconut sugar', 'antioxidants']
    },
    {
      name: 'Lakanto Sugar-Free Brownies',
      description: 'Made with monk fruit sweetener, almond flour, and cacao. Zero sugar, low glycemic, keto-friendly.',
      acneImpactScore: 25,
      whyHealthy: 'Monk fruit sweetener has zero glycemic impact. Almond flour and cacao provide healthy fats and antioxidants.',
      tags: ['brownies', 'zero sugar', 'monk fruit', 'almond flour', 'low glycemic']
    }
  ],
  
  crackers: [
    {
      name: 'Simple Mills Almond Flour Crackers',
      description: 'Made with almond flour, seeds, and olive oil. Grain-free, no refined sugar, no artificial ingredients.',
      acneImpactScore: 28,
      whyHealthy: 'Almond flour is low glycemic. Seeds provide healthy fats and fiber. Olive oil is anti-inflammatory.',
      tags: ['crackers', 'almond flour', 'grain-free', 'seeds', 'olive oil', 'fiberRich']
    },
    {
      name: 'Mary\'s Gone Crackers (Super Seed)',
      description: 'Made with whole grains, seeds, and olive oil. High fiber, no artificial preservatives.',
      acneImpactScore: 32,
      whyHealthy: 'Whole grains and seeds provide fiber and healthy fats. Olive oil instead of processed oils reduces inflammation.',
      tags: ['crackers', 'whole grain', 'seeds', 'fiberRich', 'olive oil']
    },
    {
      name: 'Crunchmaster Multi-Seed Crackers',
      description: 'Made with whole grains and seeds. High fiber, no trans fats, no artificial flavors.',
      acneImpactScore: 35,
      whyHealthy: 'Whole grains provide fiber for stable blood sugar. Seeds add healthy fats and protein.',
      tags: ['crackers', 'whole grain', 'seeds', 'fiberRich', 'no trans fats']
    }
  ],
  
  popcorn: [
    {
      name: 'LesserEvil Himalayan Pink Salt Popcorn',
      description: 'Organic popcorn popped in coconut oil with Himalayan pink salt. No artificial flavors, no preservatives.',
      acneImpactScore: 25,
      whyHealthy: 'Coconut oil provides medium-chain triglycerides. Organic popcorn is whole grain and high in fiber.',
      tags: ['popcorn', 'organic', 'coconut oil', 'whole grain', 'fiberRich', 'no artificial']
    },
    {
      name: 'SkinnyPop Original Popcorn',
      description: 'Air-popped popcorn with sunflower oil and sea salt. Lower calories, no artificial ingredients.',
      acneImpactScore: 28,
      whyHealthy: 'Air-popped uses less oil than traditional methods. Sunflower oil is less processed than hydrogenated oils.',
      tags: ['popcorn', 'air-popped', 'sunflower oil', 'lower oil', 'no artificial']
    },
    {
      name: 'Boomchickapop Lightly Salted',
      description: 'Popcorn popped in sunflower oil with sea salt. Non-GMO, no artificial flavors.',
      acneImpactScore: 30,
      whyHealthy: 'Sunflower oil is less processed than hydrogenated oils. Sea salt provides minerals without additives.',
      tags: ['popcorn', 'sunflower oil', 'non-GMO', 'sea salt', 'no artificial']
    }
  ],
  
  ice_cream: [
    {
      name: 'Halo Top (Light Ice Cream)',
      description: 'Lower sugar ice cream with protein. Many flavors under 300 calories per pint, no artificial sweeteners in some flavors.',
      acneImpactScore: 35,
      whyHealthy: 'Lower sugar reduces glycemic impact. Added protein helps with satiety. Some flavors use natural sweeteners.',
      tags: ['ice cream', 'lower sugar', 'high protein', 'lower calories']
    },
    {
      name: 'Enlightened Ice Cream',
      description: 'High-protein, lower-sugar ice cream. Made with natural sweeteners, no artificial flavors.',
      acneImpactScore: 32,
      whyHealthy: 'High protein and lower sugar help stabilize blood sugar. Natural sweeteners have lower glycemic impact.',
      tags: ['ice cream', 'high protein', 'lower sugar', 'natural sweeteners']
    },
    {
      name: 'So Delicious Dairy-Free (Coconut Milk)',
      description: 'Dairy-free ice cream made with coconut milk. Lower in dairy, some flavors have lower sugar.',
      acneImpactScore: 38,
      whyHealthy: 'Dairy-free reduces potential dairy-related inflammation. Coconut milk provides healthy fats.',
      tags: ['ice cream', 'dairy-free', 'coconut milk', 'healthyFats']
    },
    {
      name: 'Oatly Frozen Dessert',
      description: 'Plant-based frozen dessert made with oat milk. Dairy-free, some flavors have lower sugar.',
      acneImpactScore: 40,
      whyHealthy: 'Dairy-free reduces potential inflammation. Oat milk provides fiber and has lower glycemic impact than dairy.',
      tags: ['ice cream', 'dairy-free', 'oat milk', 'plant-based', 'fiberRich']
    }
  ],
  
  chocolate: [
    {
      name: 'Hu Kitchen Dark Chocolate (70%+ Cacao)',
      description: 'Simple ingredients: cacao, coconut sugar, cacao butter. No refined sugar, no dairy, no artificial ingredients.',
      acneImpactScore: 30,
      whyHealthy: 'High cacao content provides antioxidants. Coconut sugar has lower glycemic index. No dairy reduces inflammation.',
      tags: ['chocolate', 'dark chocolate', 'high cacao', 'coconut sugar', 'dairy-free', 'antioxidants']
    },
    {
      name: 'Lily\'s Sweets Dark Chocolate (Stevia)',
      description: 'Dark chocolate sweetened with stevia. Zero sugar, low glycemic, no artificial sweeteners.',
      acneImpactScore: 25,
      whyHealthy: 'Stevia has zero glycemic impact, preventing blood sugar spikes. Dark chocolate provides antioxidants.',
      tags: ['chocolate', 'dark chocolate', 'zero sugar', 'stevia', 'low glycemic', 'antioxidants']
    },
    {
      name: 'Alter Eco Dark Chocolate (70%+ Cacao)',
      description: 'Organic dark chocolate with minimal added sugar. Fair trade, no artificial ingredients.',
      acneImpactScore: 32,
      whyHealthy: 'High cacao content provides antioxidants. Organic reduces pesticide exposure. Minimal sugar reduces glycemic impact.',
      tags: ['chocolate', 'dark chocolate', 'organic', 'high cacao', 'antioxidants', 'minimal sugar']
    }
  ],
  
  cereal: [
    {
      name: 'Three Wishes Grain-Free Cereal',
      description: 'Made with chickpeas, tapioca, and coconut oil. High protein, low sugar, grain-free.',
      acneImpactScore: 28,
      whyHealthy: 'Chickpeas provide protein and fiber. Grain-free reduces refined carbs. Low sugar prevents blood sugar spikes.',
      tags: ['cereal', 'grain-free', 'high protein', 'low sugar', 'chickpeas', 'fiberRich']
    },
    {
      name: 'Magic Spoon Cereal',
      description: 'High-protein, low-sugar cereal. Made with milk protein and natural sweeteners, grain-free.',
      acneImpactScore: 30,
      whyHealthy: 'High protein helps stabilize blood sugar. Low sugar and grain-free reduce glycemic impact.',
      tags: ['cereal', 'high protein', 'low sugar', 'grain-free', 'natural sweeteners']
    },
    {
      name: 'Purely Elizabeth Granola',
      description: 'Made with whole grains, seeds, and coconut oil. Lower sugar than traditional granola, no artificial ingredients.',
      acneImpactScore: 32,
      whyHealthy: 'Whole grains and seeds provide fiber and healthy fats. Lower sugar reduces glycemic impact.',
      tags: ['cereal', 'whole grain', 'seeds', 'lower sugar', 'coconut oil', 'fiberRich']
    },
    {
      name: 'Love Grown Power O\'s',
      description: 'Made with beans, lentils, and whole grains. High protein, high fiber, lower sugar.',
      acneImpactScore: 30,
      whyHealthy: 'Beans and lentils provide plant protein and fiber. Whole grains have lower glycemic impact than refined grains.',
      tags: ['cereal', 'beans', 'lentils', 'whole grain', 'high protein', 'fiberRich']
    }
  ],
  
  soda: [
    {
      name: 'Spindrift Sparkling Water',
      description: 'Sparkling water with real fruit juice. No added sugar, no artificial flavors, no calories.',
      acneImpactScore: 10,
      whyHealthy: 'Real fruit provides natural flavor without added sugar. No calories or artificial ingredients.',
      tags: ['soda', 'sparkling water', 'fruit juice', 'no sugar', 'no calories', 'no artificial']
    },
    {
      name: 'LaCroix Sparkling Water',
      description: 'Naturally flavored sparkling water. Zero calories, zero sugar, no artificial ingredients.',
      acneImpactScore: 8,
      whyHealthy: 'Zero calories and zero sugar prevent blood sugar spikes. Natural flavors without artificial additives.',
      tags: ['soda', 'sparkling water', 'zero calories', 'zero sugar', 'no artificial']
    },
    {
      name: 'Olipop Sparkling Tonic',
      description: 'Prebiotic sparkling drink with plant fiber. Lower sugar than soda, supports gut health.',
      acneImpactScore: 25,
      whyHealthy: 'Prebiotics support gut health, which can improve skin. Lower sugar than traditional soda reduces glycemic impact.',
      tags: ['soda', 'sparkling', 'prebiotic', 'plant fiber', 'lower sugar', 'gut health']
    },
    {
      name: 'Poppi Prebiotic Soda',
      description: 'Sparkling prebiotic drink with apple cider vinegar. Lower sugar, supports gut health.',
      acneImpactScore: 28,
      whyHealthy: 'Prebiotics and apple cider vinegar support gut health. Lower sugar than traditional soda.',
      tags: ['soda', 'prebiotic', 'apple cider vinegar', 'lower sugar', 'gut health']
    }
  ]
};

/**
 * Get healthy brand recommendations for a specific food category
 */
function getHealthyBrandsForCategory(foodCategory: FoodCategory, foodName: string): HealthyBrand[] {
  const lowerName = foodName.toLowerCase();
  
  // Check for specific matches first
  if (lowerName.includes('brownie') || lowerName.includes('brownies')) {
    return HEALTHY_BRANDS_BY_CATEGORY['brownies'] || [];
  }
  
  if (lowerName.includes('cracker') || lowerName.includes('crackers')) {
    return HEALTHY_BRANDS_BY_CATEGORY['crackers'] || [];
  }
  
  if (lowerName.includes('popcorn')) {
    return HEALTHY_BRANDS_BY_CATEGORY['popcorn'] || [];
  }
  
  // Map food categories to brand categories
  const brandCategoryMap: Record<string, string> = {
    'chips': 'chips',
    'cookie': 'cookies',
    'ice_cream': 'ice_cream',
    'chocolate': 'chocolate',
    'cereal': 'cereal',
    'soda': 'soda'
  };
  
  const brandCategory = brandCategoryMap[foodCategory] || foodCategory;
  return HEALTHY_BRANDS_BY_CATEGORY[brandCategory] || [];
}

// Generic fallback alternatives (only for snacks when no category match)
const GENERIC_SNACK_ALTERNATIVES: FoodAlternative[] = [
  {
    name: 'Apple slices with almond butter',
    description: 'Natural sugars, fiber, and healthy fats - satisfying snack.',
    acneImpactScore: 25,
    tags: ['snack', 'fruit', 'healthyFats', 'fiberRich']
  },
  {
    name: 'Homemade trail mix (nuts, seeds, dark chocolate)',
    description: 'Whole foods with healthy fats, no added sugars.',
    acneImpactScore: 30,
    tags: ['snack', 'nuts', 'seeds', 'chocolate']
  },
  {
    name: 'Greek yogurt with berries',
    description: 'High-protein, lower sugar snack with antioxidants.',
    acneImpactScore: 30,
    tags: ['snack', 'yogurt', 'protein', 'berries']
  }
];

/**
 * Infers specific food category from food name and tags
 */
function inferFoodCategory(foodName: string, tags: string[]): FoodCategory {
  const lowerName = foodName.toLowerCase();
  const combinedText = `${lowerName} ${tags.join(' ')}`.toLowerCase();
  
  // Cereal
  if (lowerName.includes('cereal') || lowerName.includes('breakfast cereal') || 
      (lowerName.includes('breakfast') && (lowerName.includes('bowl') || lowerName.includes('oat')))) {
    return 'cereal';
  }
  
  // Cake
  if (lowerName.includes('cake') && !lowerName.includes('pancake') && !lowerName.includes('cookie')) {
    return 'cake';
  }
  
  // Cookie
  if (lowerName.includes('cookie') || lowerName.includes('biscuit')) {
    return 'cookie';
  }
  
  // Brownies
  if (lowerName.includes('brownie') || lowerName.includes('brownies')) {
    return 'brownies';
  }
  
  // Muffin
  if (lowerName.includes('muffin')) {
    return 'muffin';
  }
  
  // Croissant
  if (lowerName.includes('croissant')) {
    return 'croissant';
  }
  
  // Ice cream
  if (lowerName.includes('ice cream') || lowerName.includes('gelato') || lowerName.includes('frozen yogurt')) {
    return 'ice_cream';
  }
  
  // Pizza (includes calzone - folded pizza)
  if (lowerName.includes('pizza') || lowerName.includes('calzone')) {
    return 'pizza';
  }
  
  // Burger
  if (lowerName.includes('burger') || lowerName.includes('cheeseburger')) {
    return 'burger';
  }
  
  // Pasta
  if (lowerName.includes('pasta') || lowerName.includes('spaghetti') || lowerName.includes('macaroni') ||
      lowerName.includes('noodles') || lowerName.includes('lasagna') || lowerName.includes('ravioli')) {
    return 'pasta';
  }
  
  // Fries
  if (lowerName.includes('fries') || lowerName.includes('french fries') || lowerName.includes('potato fries')) {
    return 'fries';
  }
  
  // Chips
  if (lowerName.includes('chips') || lowerName.includes('crisps') || 
      (lowerName.includes('potato') && lowerName.includes('chip'))) {
    return 'chips';
  }
  
  // Soda
  if (lowerName.includes('soda') || lowerName.includes('cola') || lowerName.includes('pop') ||
      lowerName.includes('soft drink') || lowerName.includes('fizzy drink')) {
    return 'soda';
  }
  
  // Pancake
  if (lowerName.includes('pancake') || lowerName.includes('hotcake')) {
    return 'pancake';
  }
  
  // Waffle
  if (lowerName.includes('waffle')) {
    return 'waffle';
  }
  
  // Donut
  if (lowerName.includes('donut') || lowerName.includes('doughnut')) {
    return 'donut';
  }
  
  // Pie
  if (lowerName.includes('pie') && !lowerName.includes('pizza')) {
    return 'pie';
  }
  
  // Chocolate
  if (lowerName.includes('chocolate') && !lowerName.includes('chip') && !lowerName.includes('cookie')) {
    return 'chocolate';
  }
  
  // Candy
  if (lowerName.includes('candy') || lowerName.includes('sweets') || lowerName.includes('lollipop') ||
      lowerName.includes('gummy') || lowerName.includes('jelly bean')) {
    return 'candy';
  }
  
  // Bread
  if (lowerName.includes('bread') && !lowerName.includes('burger') && !lowerName.includes('sandwich')) {
    return 'bread';
  }
  
  return 'other';
}

/**
 * Infers dish type from food name and tags
 */
function inferDishType(foodName: string, tags: string[]): DishType {
  const lowerName = foodName.toLowerCase();
  const combinedText = `${lowerName} ${tags.join(' ')}`.toLowerCase();
  
  // Breakfast foods
  if (lowerName.includes('cereal') || lowerName.includes('breakfast') || 
      lowerName.includes('pancake') || lowerName.includes('waffle') || lowerName.includes('oatmeal')) {
    return 'breakfast';
  }
  
  // Desserts
  if (lowerName.includes('dessert') || lowerName.includes('cake') || lowerName.includes('cookie') || 
      lowerName.includes('ice cream') || lowerName.includes('pie') || lowerName.includes('donut') ||
      lowerName.includes('candy') || lowerName.includes('chocolate') || tags.includes('dessert')) {
    return 'dessert';
  }
  
  // Drinks
  if (lowerName.includes('drink') || lowerName.includes('soda') || lowerName.includes('juice') ||
      lowerName.includes('milkshake') || lowerName.includes('smoothie') || lowerName.includes('tea') ||
      lowerName.includes('coffee') || tags.includes('drink')) {
    return 'drink';
  }
  
  // Snacks
  if (lowerName.includes('snack') || lowerName.includes('chips') || lowerName.includes('crackers') ||
      lowerName.includes('samosa') || lowerName.includes('pakora') || lowerName.includes('nachos') ||
      tags.includes('snack')) {
    return 'snack';
  }
  
  // Sides
  if (lowerName.includes('side') || lowerName.includes('fries') || lowerName.includes('salad') ||
      lowerName.includes('raita') || lowerName.includes('plantains') || lowerName.includes('kimchi')) {
    return 'side';
  }
  
  // Default to main for full meals
  return 'main';
}

/**
 * Infers cuisine-specific dish category for better matching
 */
function inferDishCategory(cuisine: string | undefined, foodName: string, tags: string[]): string | null {
  if (!cuisine) return null;
  
  const lowerName = foodName.toLowerCase();
  const combinedText = `${lowerName} ${tags.join(' ')}`.toLowerCase();
  
  // Indian cuisine categories
  if (cuisine === 'indian') {
    if (lowerName.includes('paneer') && (lowerName.includes('butter') || lowerName.includes('masala') || lowerName.includes('korma'))) {
      return 'indian_heavy_curry_main';
    }
    if (lowerName.includes('butter chicken') || lowerName.includes('korma') || lowerName.includes('biryani')) {
      return 'indian_heavy_curry_main';
    }
    if (lowerName.includes('samosa') || lowerName.includes('pakora') || lowerName.includes('kachori')) {
      return 'indian_fried_snack';
    }
    if (lowerName.includes('roti') || lowerName.includes('naan')) {
      return 'indian_bread_main';
    }
  }
  
  // Italian cuisine categories
  if (cuisine === 'italian') {
    if (lowerName.includes('alfredo') || lowerName.includes('carbonara') || lowerName.includes('fettuccine')) {
      return 'italian_creamy_pasta';
    }
    if (lowerName.includes('pizza')) {
      return 'italian_pizza';
    }
  }
  
  // Mexican cuisine categories
  if (cuisine === 'mexican') {
    if (lowerName.includes('taco') || lowerName.includes('nachos') || lowerName.includes('quesadilla')) {
      return 'mexican_cheesy_main';
    }
  }
  
  // American fast food categories
  if (cuisine === 'american_fast_food') {
    if (lowerName.includes('burger')) {
      return 'american_burger';
    }
    if (lowerName.includes('fried chicken') || lowerName.includes('nuggets')) {
      return 'american_fried_chicken';
    }
  }
  
  return null;
}

/**
 * Get cuisine-aware food alternatives and healthy brands
 */
export function getFoodAlternatives(input: {
  foodName: string;
  cuisine?: string;
  tags: string[];
  acneImpactCategory: 'low' | 'moderate' | 'high';
  acneImpactScore?: number;
  dietaryPreference?: 'vegetarian' | 'non-vegetarian';
}): {
  alternatives: FoodAlternative[];
  brands: HealthyBrand[];
} {
  const { foodName, cuisine, tags, acneImpactCategory, acneImpactScore = 50, dietaryPreference } = input;
  
  // For foods with score <= 30, show no alternatives
  if (acneImpactScore <= 30 || acneImpactCategory === 'low') {
    return { alternatives: [], brands: [] }; // No alternatives needed for healthy foods
  }
  
  // Infer food category (cereal, cake, cookie, pizza, etc.)
  const foodCategory = inferFoodCategory(foodName, tags);
  const dishType = inferDishType(foodName, tags);
  
  const alternatives: FoodAlternative[] = [];
  const lowerName = foodName.toLowerCase();
  
  // Get healthy brands for this category (separate from alternatives)
  const healthyBrands = getHealthyBrandsForCategory(foodCategory, foodName);
  
  // For all foods with score > 30, show 7-8 alternatives
  const maxAlternatives = 8;
  
  // PRIORITY 0: Dish-specific alternatives for common dishes (highest priority)
  // Deep-fried dishes that need air-fried/baked alternatives (e.g., chicken 65)
  if (lowerName.includes('chicken 65') || lowerName.includes('chicken65') || lowerName.includes('gobi 65') || lowerName.includes('paneer 65') || lowerName.includes('fish 65') || lowerName.includes('mushroom 65')) {
    const proteinType = lowerName.includes('chicken') ? 'chicken' : lowerName.includes('gobi') ? 'cauliflower' : lowerName.includes('paneer') ? 'paneer' : lowerName.includes('fish') ? 'fish' : 'mushroom';
    alternatives.push(
      {
        name: `Air-fried ${proteinType} 65`,
        description: `Same spices and flavors, but air-fried instead of deep-fried. Much lower in oil, crispy texture, better for skin health.`,
        acneImpactScore: 45,
        tags: ['air-fried', 'indian', 'lower oil', 'spicy']
      },
      {
        name: `Baked ${proteinType} 65`,
        description: `Oven-baked version with the same spices. Minimal oil, same flavor, much healthier for acne-prone skin.`,
        acneImpactScore: 42,
        tags: ['baked', 'indian', 'lower oil', 'spicy']
      },
      {
        name: `Grilled ${proteinType} with 65 spices`,
        description: `Grilled version marinated in the same 65 spice blend. No deep-frying, lower oil, better for skin.`,
        acneImpactScore: 40,
        tags: ['grilled', 'indian', 'lower oil', 'spicy']
      }
    );
  }
  
  // Healthy Indian dishes (upma, poha, dhokla) - these are already healthy, but provide variations
  if (lowerName.includes('upma')) {
    alternatives.push(
      {
        name: 'Upma with more vegetables and less oil',
        description: 'Traditional upma with extra vegetables (carrots, peas, bell peppers) and minimal oil. More fiber, more nutrients.',
        acneImpactScore: 18,
        tags: ['indian', 'upma', 'vegetables', 'lower oil']
      },
      {
        name: 'Quinoa upma',
        description: 'Upma made with quinoa instead of semolina. Higher protein, lower glycemic, more nutrients.',
        acneImpactScore: 15,
        tags: ['indian', 'upma', 'quinoa', 'whole grain']
      },
      {
        name: 'Millet upma',
        description: 'Upma made with millet instead of semolina. Higher fiber, lower glycemic, better for skin.',
        acneImpactScore: 16,
        tags: ['indian', 'upma', 'millet', 'whole grain']
      }
    );
  }
  
  if (lowerName.includes('poha')) {
    alternatives.push(
      {
        name: 'Poha with more vegetables and less oil',
        description: 'Traditional poha with extra vegetables (onions, peas, carrots) and minimal oil. More fiber, more nutrients.',
        acneImpactScore: 20,
        tags: ['indian', 'poha', 'vegetables', 'lower oil']
      },
      {
        name: 'Quinoa poha',
        description: 'Poha made with quinoa instead of flattened rice. Higher protein, lower glycemic, more nutrients.',
        acneImpactScore: 18,
        tags: ['indian', 'poha', 'quinoa', 'whole grain']
      },
      {
        name: 'Brown rice poha',
        description: 'Poha made with brown rice flakes instead of white. More fiber, lower glycemic, better for skin.',
        acneImpactScore: 19,
        tags: ['indian', 'poha', 'brown rice', 'whole grain']
      }
    );
  }
  
  if (lowerName.includes('dhokla')) {
    alternatives.push(
      {
        name: 'Steamed dhokla with less oil',
        description: 'Traditional dhokla steamed (not fried) with minimal oil in tempering. High protein, low fat, good for skin.',
        acneImpactScore: 22,
        tags: ['indian', 'dhokla', 'steamed', 'lower oil']
      },
      {
        name: 'Quinoa dhokla',
        description: 'Dhokla made with quinoa flour instead of gram flour. Higher protein, lower glycemic, more nutrients.',
        acneImpactScore: 20,
        tags: ['indian', 'dhokla', 'quinoa', 'whole grain']
      },
      {
        name: 'Oats dhokla',
        description: 'Dhokla made with oats flour. Higher fiber, lower glycemic, better for skin health.',
        acneImpactScore: 21,
        tags: ['indian', 'dhokla', 'oats', 'whole grain']
      }
    );
  }
  
  // STRICT RULE: Never mix food types - pizza → only pizza alternatives, pasta → only pasta alternatives
  // If a category is detected, ONLY show that category's alternatives (same food type)
  
  // PRIORITY 1: Category-specific alternatives (pizza → pizza, burger → burger, pasta → pasta, etc.)
  // Use for ALL categories - ensures same-type alternatives (no spaghetti when user wants pizza)
  if (foodCategory !== 'other' && CATEGORY_ALTERNATIVES[foodCategory] && CATEGORY_ALTERNATIVES[foodCategory].length > 0) {
    const categoryAlternatives = CATEGORY_ALTERNATIVES[foodCategory];
    
    const alternativesWithRecipes = categoryAlternatives.map((alt, idx) => {
      const recipe = getOrCreateRecipe(alt, cuisine);
      const id = generateAlternativeId(alt.name, idx);
      return { ...alt, id, recipe };
    });
    alternatives.push(...alternativesWithRecipes);
    
    // If we have category alternatives, ONLY use those - never mix with cuisine
    // Limit to maxAlternatives and ensure ALL have recipes
    let uniqueAlternatives = alternatives
      .filter((alt, index, self) => index === self.findIndex(a => a.name === alt.name))
      .slice(0, maxAlternatives);
    
    const finalAlternatives = uniqueAlternatives.map((alt, idx) => {
      const id = alt.id || generateAlternativeId(alt.name, idx);
      const recipe = alt.recipe || getOrCreateRecipe(alt, cuisine);
      return { ...alt, id, recipe };
    });

    return { alternatives: finalAlternatives, brands: healthyBrands };
  }

  // PRIORITY 2: If cuisine is detected (and no category), show that cuisine's alternatives
  // CRITICAL: Filter to same food type - pizza → only pizza, pasta → only pasta (no mixing)
  if (cuisine && CUISINE_ALTERNATIVES[cuisine]) {
    // Helper: filter to same-type alternatives (e.g., only pizza when food is pizza)
    const filterBySameType = (dishes: FoodAlternative[]) => {
      if (lowerName.includes('pizza')) return dishes.filter(d => d.tags.some(t => t.toLowerCase().includes('pizza')));
      if (lowerName.includes('pasta') || lowerName.includes('spaghetti') || lowerName.includes('noodle') || lowerName.includes('lasagna')) return dishes.filter(d => d.tags.some(t => t.includes('pasta') || t.includes('noodle')));
      if (lowerName.includes('burger')) return dishes.filter(d => d.tags.some(t => t.includes('burger')));
      if (lowerName.includes('taco') || lowerName.includes('burrito')) return dishes.filter(d => d.tags.some(t => t.includes('taco') || t.includes('burrito')));
      return dishes;
    };

    if (CUISINE_ALTERNATIVES[cuisine][dishType] && CUISINE_ALTERNATIVES[cuisine][dishType].length > 0) {
      const rawCuisineAlternatives = CUISINE_ALTERNATIVES[cuisine][dishType];
      const cuisineAlternatives = filterBySameType(rawCuisineAlternatives);
      const alternativesWithRecipes = cuisineAlternatives.map((alt, idx) => {
        const recipe = getOrCreateRecipe(alt, cuisine);
        const id = generateAlternativeId(alt.name, idx);
        return { ...alt, id, recipe };
      });
      alternatives.push(...alternativesWithRecipes);
    }

    // Only pull from other dish types if we have very few AND no same-type filter applies
    // Never add pasta when user wanted pizza
    if (alternatives.length < maxAlternatives * 0.5) {
      const sameTypeFilter = lowerName.includes('pizza') || lowerName.includes('pasta') || lowerName.includes('burger') || lowerName.includes('taco');
      if (!sameTypeFilter) {
        for (const [type, dishes] of Object.entries(CUISINE_ALTERNATIVES[cuisine])) {
          if (type !== dishType && dishes.length > 0) {
            const additionalAlternatives = dishes.map((alt, dishIdx) => {
              const recipe = getRecipeForAlternative(alt.name, cuisine);
              const id = generateAlternativeId(alt.name, dishIdx);
              return { ...alt, id, recipe };
            });
            alternatives.push(...additionalAlternatives);
            if (alternatives.length >= maxAlternatives * 2) break;
          }
        }
      }
    }
    
    // If we have cuisine alternatives, ONLY use those - never mix with other cuisines
    // Remove duplicates and limit
    let uniqueAlternatives = alternatives
      .filter((alt, index, self) => index === self.findIndex(a => a.name === alt.name))
      .slice(0, maxAlternatives);
    
    // Ensure ALL have recipes and unique IDs
    const finalAlternatives = uniqueAlternatives.map((alt, idx) => {
      const id = alt.id || generateAlternativeId(alt.name, idx);
      const recipe = alt.recipe || getOrCreateRecipe(alt, cuisine);
      return { ...alt, id, recipe };
    });

    return { alternatives: finalAlternatives, brands: healthyBrands };
  }

  // PRIORITY 3: Generic dessert alternatives (only if no category and no cuisine, but it's a dessert)
  if (dishType === 'dessert' && 
      CUISINE_ALTERNATIVES['generic_dessert'] && CUISINE_ALTERNATIVES['generic_dessert'][dishType]) {
    const genericAlternatives = CUISINE_ALTERNATIVES['generic_dessert'][dishType];
    const alternativesWithRecipes = genericAlternatives.map((alt, idx) => {
      const recipe = getOrCreateRecipe(alt, undefined);
      const id = generateAlternativeId(alt.name, idx);
      return { ...alt, id, recipe };
    });
    alternatives.push(...alternativesWithRecipes);
  }
  
  // PRIORITY 4: Generic snacks (only if it's a snack and we still don't have enough)
  if (alternatives.length < maxAlternatives && dishType === 'snack') {
    const genericAlternatives = GENERIC_SNACK_ALTERNATIVES.map((alt, idx) => {
      const recipe = getOrCreateRecipe(alt, cuisine);
      const id = generateAlternativeId(alt.name, idx);
      return { ...alt, id, recipe };
    });
    alternatives.push(...genericAlternatives);
  }
  
  // PRIORITY 5: Better substitutes for stir-fried dishes (like fried rice, fried noodles)
  // Stir-fried dishes should get lighter stir-fry versions, NOT baked/air-fried/grilled
  if (tags.includes('stirFried') && alternatives.length < maxAlternatives) {
    const lowerName = foodName.toLowerCase();
    const isRice = lowerName.includes('rice') || lowerName.includes('fried rice');
    const isNoodles = lowerName.includes('noodle') || lowerName.includes('chow mein') || lowerName.includes('lo mein') || lowerName.includes('yakisoba') || lowerName.includes('pad thai');
    const isChinese = cuisine === 'chinese' || cuisine === 'chinese_american';
    
    const stirFriedAlternatives: FoodAlternative[] = [];
    
    if (isRice) {
      // Fried rice alternatives
      stirFriedAlternatives.push(
        {
          name: 'Veggie-loaded brown rice stir-fry',
          description: 'Brown rice stir-fried with extra vegetables, less oil, and lean protein. Lower glycemic load, more fiber, better for skin.',
          acneImpactScore: 38,
          tags: ['stir-fried', 'brown rice', 'vegetables', 'lower oil', 'whole grain']
        },
        {
          name: 'Half rice, half cauliflower fried rice',
          description: 'Mix of brown rice and cauliflower rice with veggies and egg; lighter carbs and more fiber. Much lower glycemic impact.',
          acneImpactScore: 35,
          tags: ['stir-fried', 'cauliflower rice', 'brown rice', 'vegetables', 'lower carb']
        },
        {
          name: 'Steamed rice with side stir-fried veggies',
          description: 'Simpler rice base with separate lightly stir-fried veggies; easier on blood sugar and oil load. Less oil overall.',
          acneImpactScore: 40,
          tags: ['steamed rice', 'stir-fried vegetables', 'lower oil', 'separate preparation']
        },
        {
          name: 'Quinoa fried rice with vegetables',
          description: 'Quinoa instead of white rice, stir-fried with vegetables and minimal oil. Higher protein, lower glycemic, better for skin.',
          acneImpactScore: 32,
          tags: ['stir-fried', 'quinoa', 'vegetables', 'lower oil', 'whole grain']
        }
      );
    } else if (isNoodles) {
      // Fried noodles alternatives
      stirFriedAlternatives.push(
        {
          name: 'Lighter noodle stir-fry with more vegetables',
          description: 'Same noodles, but with extra vegetables and less oil. More fiber, less refined carbs per serving.',
          acneImpactScore: 42,
          tags: ['stir-fried', 'noodles', 'vegetables', 'lower oil']
        },
        {
          name: 'Zucchini noodles (zoodles) stir-fry',
          description: 'Spiralized zucchini instead of noodles, stir-fried with vegetables and minimal oil. Much lower carbs, more nutrients.',
          acneImpactScore: 30,
          tags: ['stir-fried', 'zucchini noodles', 'vegetables', 'lower carb', 'no refined carbs']
        },
        {
          name: 'Brown rice noodles with vegetables',
          description: 'Brown rice noodles instead of white, stir-fried with extra vegetables and less oil. Lower glycemic, more fiber.',
          acneImpactScore: 38,
          tags: ['stir-fried', 'brown rice noodles', 'vegetables', 'whole grain', 'lower oil']
        },
        {
          name: 'Shirataki noodles stir-fry',
          description: 'Shirataki (konjac) noodles instead of regular noodles, stir-fried with vegetables. Very low carb, minimal calories.',
          acneImpactScore: 28,
          tags: ['stir-fried', 'shirataki noodles', 'vegetables', 'very low carb']
        }
      );
    } else {
      // Generic stir-fry alternatives
      stirFriedAlternatives.push(
        {
          name: 'Lighter stir-fry with less oil and more vegetables',
          description: 'Same stir-fry, but with extra vegetables and minimal oil. More fiber, less oil, better for skin health.',
          acneImpactScore: 40,
          tags: ['stir-fried', 'vegetables', 'lower oil']
        },
        {
          name: 'Steamed vegetables with light sauce',
          description: 'Steamed vegetables instead of stir-fried, with a light sauce. No oil, more nutrients preserved.',
          acneImpactScore: 35,
          tags: ['steamed', 'vegetables', 'no oil', 'light sauce']
        }
      );
    }
    
    // Add these stir-fried alternatives
    const stirFriedWithRecipes = stirFriedAlternatives.map(alt => {
      const recipe = getOrCreateRecipe(alt, cuisine);
      return { ...alt, recipe };
    });
    alternatives.push(...stirFriedWithRecipes);
  }
  
  // PRIORITY 6: Generate healthier alternatives for ANY unrecognized dish
  // CRITICAL: If we have a known category, use category-specific alternatives only (same food type)
  if (alternatives.length < maxAlternatives && acneImpactScore > 30) {
    const lowerName = foodName.toLowerCase();
    // Same-type check: use category alternatives for recognized foods even in fallback
    const fallbackCategory = inferFoodCategory(foodName, tags);
    if (fallbackCategory !== 'other' && CATEGORY_ALTERNATIVES[fallbackCategory]?.length > 0) {
      const categoryAlts = CATEGORY_ALTERNATIVES[fallbackCategory].map((alt, idx) => {
        const recipe = getOrCreateRecipe(alt, cuisine);
        const id = alt.id || generateAlternativeId(alt.name, idx);
        return { ...alt, id, recipe };
      });
      alternatives.push(...categoryAlts);
    } else {
    const hasRice = lowerName.includes('rice') || lowerName.includes('biryani') || lowerName.includes('pulao');
    const hasNoodles = lowerName.includes('noodle') || lowerName.includes('pasta') || lowerName.includes('spaghetti');
    const hasBread = lowerName.includes('bread') || lowerName.includes('roti') || lowerName.includes('naan') || lowerName.includes('pita') || lowerName.includes('tortilla');
    const hasCurry = lowerName.includes('curry') || lowerName.includes('masala') || lowerName.includes('sauce') || lowerName.includes('gravy');
    const hasSoup = lowerName.includes('soup') || lowerName.includes('stew') || lowerName.includes('dal') || lowerName.includes('sambar') || lowerName.includes('rasam');
    const hasMeat = lowerName.includes('chicken') || lowerName.includes('beef') || lowerName.includes('pork') || lowerName.includes('lamb') || lowerName.includes('meat') || lowerName.includes('fish');
    const hasVegetable = lowerName.includes('vegetable') || lowerName.includes('veggie') || lowerName.includes('sabzi') || lowerName.includes('subzi');
    
    const genericAlternatives: FoodAlternative[] = [];
    
    // Generate cuisine-appropriate alternatives based on detected cuisine
    if (cuisine) {
      // Indian cuisine alternatives
      if (cuisine === 'indian') {
        if (hasSoup || lowerName.includes('dal') || lowerName.includes('sambar') || lowerName.includes('rasam')) {
          genericAlternatives.push(
            {
              id: generateAlternativeId('Lighter dal/sambar with brown rice', genericAlternatives.length),
              name: 'Lighter dal/sambar with brown rice',
              description: 'Same lentil/vegetable soup, but with less oil and served with brown rice instead of white. More fiber, lower glycemic impact.',
              acneImpactScore: 35,
              tags: ['indian', 'dal', 'brown rice', 'lower oil', 'whole grain']
            },
            {
              name: 'Dal/sambar with quinoa or millet',
              description: 'Traditional lentil soup with quinoa or millet instead of white rice. Higher protein, lower glycemic, better for skin.',
              acneImpactScore: 32,
              tags: ['indian', 'dal', 'quinoa', 'millet', 'whole grain']
            },
            {
              name: 'Vegetable-loaded dal/sambar',
              description: 'Extra vegetables added to the soup, less oil, served with whole grain. More nutrients, less refined carbs.',
              acneImpactScore: 38,
              tags: ['indian', 'dal', 'vegetables', 'whole grain', 'lower oil']
            }
          );
        } else if (hasCurry || hasMeat) {
          genericAlternatives.push(
            {
              name: 'Lighter curry with less oil and whole grain',
              description: 'Same curry, but cooked with minimal oil and served with brown rice or whole-wheat roti. Lower glycemic, more fiber.',
              acneImpactScore: 42,
              tags: ['indian', 'curry', 'whole grain', 'lower oil']
            },
            {
              name: 'Grilled or baked version with lighter sauce',
              description: 'Same protein, but grilled or baked instead of fried, with a lighter curry sauce using less oil and cream.',
              acneImpactScore: 40,
              tags: ['indian', 'grilled', 'baked', 'lighter sauce', 'lower oil']
            },
            {
              name: 'Vegetable-heavy curry with whole grain',
              description: 'More vegetables, less meat, minimal oil, served with brown rice or whole-wheat roti. More fiber, less saturated fat.',
              acneImpactScore: 38,
              tags: ['indian', 'curry', 'vegetables', 'whole grain', 'lower oil']
            }
          );
        } else if (hasRice) {
          genericAlternatives.push(
            {
              name: 'Brown rice version with more vegetables',
              description: 'Same dish, but with brown rice instead of white, and extra vegetables. Lower glycemic, more fiber, better for skin.',
              acneImpactScore: 40,
              tags: ['indian', 'brown rice', 'vegetables', 'whole grain']
            },
            {
              name: 'Quinoa or millet version',
              description: 'Quinoa or millet instead of white rice, with the same accompaniments. Higher protein, lower glycemic impact.',
              acneImpactScore: 35,
              tags: ['indian', 'quinoa', 'millet', 'whole grain']
            }
          );
        } else {
          // Generic Indian alternative
          genericAlternatives.push(
            {
              name: 'Lighter version with less oil and whole grain',
              description: 'Same dish, but prepared with minimal oil and served with whole grains. Lower glycemic, more fiber, better for skin health.',
              acneImpactScore: 42,
              tags: ['indian', 'lower oil', 'whole grain']
            },
            {
              name: 'Vegetable-enhanced version',
              description: 'Same dish with extra vegetables and less oil. More nutrients, more fiber, lower calorie density.',
              acneImpactScore: 38,
              tags: ['indian', 'vegetables', 'lower oil']
            }
          );
        }
      }
      // Chinese cuisine alternatives
      else if (cuisine === 'chinese' || cuisine === 'chinese_american') {
        if (hasMeat && (tags.includes('deepFried') || lowerName.includes('chicken') || lowerName.includes('beef') || lowerName.includes('pork'))) {
          // Chinese chicken/meat dishes (like La Zi Ji, Kung Pao, General Tso's)
          const proteinType = lowerName.includes('chicken') ? 'chicken' : lowerName.includes('beef') ? 'beef' : lowerName.includes('pork') ? 'pork' : 'protein';
          genericAlternatives.push(
            {
              name: `Air-fried or baked ${proteinType} with same spices`,
              description: `Same ${proteinType} dish with the same Chinese spices and flavors, but air-fried or baked instead of deep-fried. Much lower in oil, crispy texture, better for skin health.`,
              acneImpactScore: 45,
              tags: ['chinese', 'air-fried', 'baked', proteinType, 'lower oil', 'spicy']
            },
            {
              name: `Grilled ${proteinType} with lighter sauce`,
              description: `Grilled ${proteinType} marinated in the same spices, with a lighter sauce using less oil and sugar. No deep-frying, better for acne health.`,
              acneImpactScore: 42,
              tags: ['chinese', 'grilled', proteinType, 'lighter sauce', 'lower oil']
            },
            {
              name: `Stir-fried ${proteinType} with more vegetables and less oil`,
              description: `Same ${proteinType} dish stir-fried with extra vegetables and minimal oil. More fiber, less oil, same flavors.`,
              acneImpactScore: 48,
              tags: ['chinese', 'stir-fried', proteinType, 'vegetables', 'lower oil']
            },
            {
              name: `Steamed ${proteinType} with Chinese spices`,
              description: `Steamed ${proteinType} with the same Chinese spices and aromatics. No oil, clean preparation, better for skin.`,
              acneImpactScore: 40,
              tags: ['chinese', 'steamed', proteinType, 'spices', 'no oil']
            }
          );
        } else if (hasRice) {
          genericAlternatives.push(
            {
              name: 'Brown rice version with more vegetables',
              description: 'Same Chinese dish, but with brown rice instead of white, and extra vegetables. Lower glycemic, more fiber, better for skin.',
              acneImpactScore: 40,
              tags: ['chinese', 'brown rice', 'vegetables', 'whole grain']
            },
            {
              name: 'Quinoa fried rice with vegetables',
              description: 'Quinoa instead of white rice, stir-fried with vegetables and minimal oil. Higher protein, lower glycemic, better for skin.',
              acneImpactScore: 38,
              tags: ['chinese', 'quinoa', 'vegetables', 'whole grain', 'lower oil']
            }
          );
        } else if (hasNoodles) {
          genericAlternatives.push(
            {
              name: 'Whole grain noodles with more vegetables',
              description: 'Same Chinese dish, but with whole wheat or brown rice noodles, and extra vegetables. Lower glycemic, more fiber.',
              acneImpactScore: 42,
              tags: ['chinese', 'whole grain', 'noodles', 'vegetables']
            },
            {
              name: 'Zucchini noodles (zoodles) stir-fry',
              description: 'Zucchini noodles instead of regular noodles, stir-fried with vegetables and minimal oil. Much lower carbs, more nutrients.',
              acneImpactScore: 35,
              tags: ['chinese', 'zucchini noodles', 'vegetables', 'lower carb']
            }
          );
        } else {
          // Generic Chinese alternative
          genericAlternatives.push(
            {
              name: 'Lighter Chinese version with less oil and more vegetables',
              description: 'Same Chinese dish, but prepared with minimal oil and extra vegetables. More fiber, more nutrients, better for skin health.',
              acneImpactScore: 42,
              tags: ['chinese', 'vegetables', 'lower oil']
            }
          );
        }
      }
      // Add similar logic for other major cuisines...
      else {
        // Generic fallback for any other cuisine - ensure dish-type-specific alternatives
        if (hasMeat && tags.includes('deepFried')) {
          const proteinType = lowerName.includes('chicken') ? 'chicken' : lowerName.includes('beef') ? 'beef' : lowerName.includes('pork') ? 'pork' : lowerName.includes('fish') ? 'fish' : 'protein';
          genericAlternatives.push(
            {
              name: `Air-fried or baked ${proteinType} (${cuisine} style)`,
              description: `Same ${proteinType} dish with ${cuisine} flavors, but air-fried or baked instead of deep-fried. Much lower in oil, better for skin health.`,
              acneImpactScore: 45,
              tags: [cuisine, 'air-fried', 'baked', proteinType, 'lower oil']
            },
            {
              name: `Grilled ${proteinType} (${cuisine} style)`,
              description: `Grilled ${proteinType} with ${cuisine} spices and marinade. No deep-frying, lower oil, better for acne health.`,
              acneImpactScore: 42,
              tags: [cuisine, 'grilled', proteinType, 'lower oil']
            }
          );
        } else if (hasMeat && !tags.includes('deepFried')) {
          const proteinType = lowerName.includes('chicken') ? 'chicken' : lowerName.includes('beef') ? 'beef' : lowerName.includes('pork') ? 'pork' : lowerName.includes('fish') ? 'fish' : 'protein';
          genericAlternatives.push(
            {
              name: `Lighter ${proteinType} dish with more vegetables`,
              description: `Same ${proteinType} dish with ${cuisine} flavors, but with extra vegetables and minimal oil. More fiber, more nutrients.`,
              acneImpactScore: 42,
              tags: [cuisine, proteinType, 'vegetables', 'lower oil']
            }
          );
        } else if (hasRice) {
          genericAlternatives.push(
            {
              name: `Brown rice version with more vegetables (${cuisine} style)`,
              description: `Same ${cuisine} dish, but with brown rice instead of white, and extra vegetables. Lower glycemic, more fiber, better for skin.`,
              acneImpactScore: 40,
              tags: [cuisine, 'brown rice', 'vegetables', 'whole grain']
            }
          );
        } else if (hasNoodles) {
          genericAlternatives.push(
            {
              name: `Whole grain noodles with more vegetables (${cuisine} style)`,
              description: `Same ${cuisine} dish, but with whole wheat or brown rice noodles, and extra vegetables. Lower glycemic, more fiber.`,
              acneImpactScore: 42,
              tags: [cuisine, 'whole grain', 'noodles', 'vegetables']
            }
          );
        } else if (hasCurry || hasSoup) {
          genericAlternatives.push(
            {
              name: `Lighter version with less oil (${cuisine} style)`,
              description: `Same ${cuisine} dish, but prepared with minimal oil and more vegetables. Lower calorie density, more nutrients.`,
              acneImpactScore: 42,
              tags: [cuisine, 'lower oil', 'vegetables']
            }
          );
        } else {
          // Generic fallback - but still cuisine-appropriate
          genericAlternatives.push(
            {
              name: `Lighter version with more vegetables and less oil (${cuisine} style)`,
              description: `Same ${cuisine} dish, but with extra vegetables and minimal oil. More fiber, more nutrients, better for skin health.`,
              acneImpactScore: 40,
              tags: [cuisine, 'vegetables', 'lower oil']
            }
          );
        }
      }
    } else {
      // No cuisine detected - provide generic alternatives
      if (hasRice) {
        genericAlternatives.push(
          {
            name: 'Brown rice version with more vegetables',
            description: 'Same dish, but with brown rice instead of white, and extra vegetables. Lower glycemic, more fiber, better for skin.',
            acneImpactScore: 40,
            tags: ['brown rice', 'vegetables', 'whole grain']
          }
        );
      }
      if (hasNoodles) {
        genericAlternatives.push(
          {
            name: 'Whole grain noodles with more vegetables',
            description: 'Same dish, but with whole wheat or brown rice noodles, and extra vegetables. Lower glycemic, more fiber.',
            acneImpactScore: 42,
            tags: ['whole grain', 'noodles', 'vegetables']
          }
        );
      }
      if (tags.includes('deepFried')) {
        genericAlternatives.push(
          {
            name: 'Baked or grilled version',
            description: 'Same dish, but baked or grilled instead of deep-fried. Much lower in oil, better for skin health.',
            acneImpactScore: 40,
            tags: ['baked', 'grilled', 'lower oil']
          }
        );
      }
      if (genericAlternatives.length === 0) {
        genericAlternatives.push(
          {
            name: 'Lighter version with more vegetables and less oil',
            description: 'Same dish, but with extra vegetables and minimal oil. More fiber, more nutrients, better for skin health.',
            acneImpactScore: 40,
            tags: ['vegetables', 'lower oil']
          }
        );
      }
    }
    
    // Add these generic alternatives
    const genericWithRecipes = genericAlternatives.map((alt, idx) => {
      const recipe = getOrCreateRecipe(alt, cuisine);
      const id = alt.id || generateAlternativeId(alt.name, idx);
      return { ...alt, id, recipe };
    });
    alternatives.push(...genericWithRecipes);
    }
  }

  // PRIORITY 7: Generic healthier alternatives for ANY deep-fried food
  // This ensures ALL deep-fried foods get healthier alternatives, regardless of cuisine/category
  if (tags.includes('deepFried') && alternatives.length < maxAlternatives) {
    const lowerName = foodName.toLowerCase();
    const isChicken = lowerName.includes('chicken');
    const isFish = lowerName.includes('fish') || lowerName.includes('salmon') || lowerName.includes('tuna') || lowerName.includes('cod');
    const isVegetable = lowerName.includes('vegetable') || lowerName.includes('veggie') || lowerName.includes('zucchini') || lowerName.includes('eggplant') || lowerName.includes('mushroom');
    const isMeat = lowerName.includes('beef') || lowerName.includes('pork') || lowerName.includes('lamb') || lowerName.includes('meat');
    const hasSauce = lowerName.includes('sauce') || lowerName.includes('glaze') || lowerName.includes('marinade') || tags.includes('sweetSauce');
    
    // Generate generic healthier alternatives based on food type
    const genericDeepFriedAlternatives: FoodAlternative[] = [];
    
    if (isChicken) {
      genericDeepFriedAlternatives.push(
        {
          name: 'Baked or air-fried chicken',
          description: 'Same chicken, but baked or air-fried instead of deep-fried. Much lower in oil and processed fats, better for skin health.',
          acneImpactScore: 40,
          tags: ['baked', 'air-fried', 'chicken', 'lower oil', 'no deep-frying']
        },
        {
          name: 'Grilled chicken',
          description: 'Grilled chicken with minimal oil. No deep-frying, lower in processed fats, much better for acne health.',
          acneImpactScore: 38,
          tags: ['grilled', 'chicken', 'minimal oil', 'no deep-frying']
        },
        {
          name: 'Oven-baked chicken with herbs',
          description: 'Chicken baked in the oven with herbs and spices. No deep-frying, lower oil, cleaner preparation.',
          acneImpactScore: 42,
          tags: ['baked', 'chicken', 'herbs', 'lower oil']
        }
      );
    } else if (isFish) {
      genericDeepFriedAlternatives.push(
        {
          name: 'Baked or grilled fish',
          description: 'Fish baked or grilled instead of deep-fried. Lower in oil, higher in omega-3s, much better for skin health.',
          acneImpactScore: 35,
          tags: ['baked', 'grilled', 'fish', 'omega-3', 'lower oil']
        },
        {
          name: 'Pan-seared fish with herbs',
          description: 'Fish pan-seared with minimal oil and fresh herbs. No deep-frying, lower in processed fats.',
          acneImpactScore: 38,
          tags: ['pan-seared', 'fish', 'herbs', 'minimal oil']
        }
      );
    } else if (isVegetable) {
      genericDeepFriedAlternatives.push(
        {
          name: 'Roasted vegetables',
          description: 'Vegetables roasted in the oven with minimal oil. No deep-frying, higher in fiber and nutrients.',
          acneImpactScore: 30,
          tags: ['roasted', 'vegetables', 'fiber', 'minimal oil']
        },
        {
          name: 'Steamed vegetables with herbs',
          description: 'Vegetables steamed and seasoned with herbs. No oil, no deep-frying, very clean preparation.',
          acneImpactScore: 25,
          tags: ['steamed', 'vegetables', 'herbs', 'no oil']
        },
        {
          name: 'Air-fried vegetables',
          description: 'Vegetables air-fried with minimal oil. Crispy texture without deep-frying, much lower in oil.',
          acneImpactScore: 32,
          tags: ['air-fried', 'vegetables', 'minimal oil', 'crispy']
        }
      );
    } else if (isMeat) {
      genericDeepFriedAlternatives.push(
        {
          name: 'Grilled or baked meat',
          description: 'Meat grilled or baked instead of deep-fried. Much lower in oil and processed fats, better for skin health.',
          acneImpactScore: 45,
          tags: ['grilled', 'baked', 'meat', 'lower oil']
        },
        {
          name: 'Pan-seared meat with herbs',
          description: 'Meat pan-seared with minimal oil and fresh herbs. No deep-frying, cleaner preparation.',
          acneImpactScore: 48,
          tags: ['pan-seared', 'meat', 'herbs', 'minimal oil']
        }
      );
    } else {
      // Generic fallback for any deep-fried food
      genericDeepFriedAlternatives.push(
        {
          name: 'Baked version',
          description: 'Same dish, but baked instead of deep-fried. Much lower in oil and processed fats, better for skin health.',
          acneImpactScore: 42,
          tags: ['baked', 'lower oil', 'no deep-frying']
        },
        {
          name: 'Air-fried version',
          description: 'Same dish, but air-fried instead of deep-fried. Crispy texture with much less oil, better for acne health.',
          acneImpactScore: 40,
          tags: ['air-fried', 'lower oil', 'crispy']
        },
        {
          name: 'Grilled version',
          description: 'Same dish, but grilled instead of deep-fried. Minimal oil, no deep-frying, much better for skin.',
          acneImpactScore: 38,
          tags: ['grilled', 'minimal oil', 'no deep-frying']
        }
      );
    }
    
    // If the original has a sauce/glaze, suggest lighter sauce versions
    if (hasSauce) {
      genericDeepFriedAlternatives.push(
        {
          name: 'Baked version with lighter sauce',
          description: 'Baked instead of deep-fried, with a lighter sauce using less sugar and oil. Much better for skin health.',
          acneImpactScore: 45,
          tags: ['baked', 'lighter sauce', 'lower sugar', 'lower oil']
        }
      );
    }
    
    // Add these generic alternatives
    const genericWithRecipes = genericDeepFriedAlternatives.map(alt => {
      const recipe = getRecipeForAlternative(alt.name, cuisine);
      return recipe ? { ...alt, recipe } : alt;
    });
    alternatives.push(...genericWithRecipes);
  }
  
  // Remove duplicates by name
  let uniqueAlternatives = alternatives
    .filter((alt, index, self) => index === self.findIndex(a => a.name === alt.name));
  
  // Limit to maxAlternatives and ensure ALL have recipes
  uniqueAlternatives = uniqueAlternatives.slice(0, maxAlternatives);
  
  // Final pass: Ensure ALL alternatives have recipes attached and unique IDs
  let finalAlternatives = uniqueAlternatives.map((alt, idx) => {
    const id = alt.id || generateAlternativeId(alt.name, idx);
    const recipe = alt.recipe || getOrCreateRecipe(alt, cuisine);
    return { ...alt, id, recipe };
  });
  
  // Filter by dietary preference if vegetarian
  if (dietaryPreference === 'vegetarian') {
    const meatKeywords = ['chicken', 'beef', 'pork', 'lamb', 'turkey', 'fish', 'salmon', 'tuna', 'shrimp', 'meat', 'burger', 'steak', 'wings', 'ribs', 'bacon', 'sausage', 'ham', 'duck', 'seafood'];
    finalAlternatives = finalAlternatives.filter(alt => {
      const altNameLower = alt.name.toLowerCase();
      const descLower = alt.description.toLowerCase();
      return !meatKeywords.some(keyword => altNameLower.includes(keyword) || descLower.includes(keyword));
    });
  }
  
  // Return alternatives and brands separately
  return { alternatives: finalAlternatives, brands: healthyBrands };
}
